<template>
    <div>
        <div class="marketplace-container animated fadeIn">
            <!-- Search and Filters Section -->
            <div class="marketplace-header mb-4">
                <div class="search-wrapper">
                    <b-input-group size="lg">
                        <b-input-group-prepend>
                            <b-input-group-text class="search-icon">
                                <b-icon icon="search"></b-icon>
                            </b-input-group-text>
                        </b-input-group-prepend>
                        <b-form-input 
                            v-model="searchKeyword" 
                            :placeholder="$t('marketplace.search')" 
                            type="search"
                            class="search-input"
                        />
                    </b-input-group>
                </div>
            </div>

            <!-- Filters Section -->
            <div class="marketplace-filters mb-4">
                <div class="category-pills">
                    <button 
                        class="category-pill"
                        :class="{ active: selectedCategory === 'all' }"
                        @click="filterByCategory('all')"
                    >
                        <span class="pill-label">{{ $t('marketplace.all') }}</span>
                    </button>
                    <button 
                        class="category-pill"
                        :class="{ active: selectedCategory === 'integrations' }"
                        @click="filterByCategory('integrations')"
                    >
                        <b-icon icon="puzzle" class="pill-icon"></b-icon>
                        <span class="pill-label">{{ $t('marketplace.integrations') }}</span>
                        <span class="pill-count">{{ integrations.length }}</span>
                    </button>
                    <button 
                        class="category-pill"
                        :class="{ active: selectedCategory === 'templates' }"
                        @click="filterByCategory('templates')"
                    >
                        <b-icon icon="file-earmark-text" class="pill-icon"></b-icon>
                        <span class="pill-label">{{ $t('marketplace.templates') }}</span>
                        <span class="pill-count">{{ templates.length }}</span>
                    </button>
                    <button 
                        class="category-pill"
                        :class="{ active: selectedCategory === 'apps' }"
                        @click="filterByCategory('apps')"
                    >
                        <b-icon icon="grid-3x3" class="pill-icon"></b-icon>
                        <span class="pill-label">{{ $t('marketplace.apps') }}</span>
                        <span class="pill-count">{{ apps.length }}</span>
                    </button>
                </div>
                <button
                    class="owned-filter"
                    :class="{ active: showOnlyOwned }"
                    @click="showOnlyOwned = !showOnlyOwned"
                >
                    <b-icon :icon="showOnlyOwned ? 'check-circle-fill' : 'check-circle'"></b-icon>
                    <span>{{ $t('marketplace.owned') }}</span>
                </button>
                <button
                    class="owned-filter redeem-code-btn"
                    @click="showRedeemCodeModal = true"
                >
                    <b-icon icon="key"></b-icon>
                    <span>{{ $t('redeemCode.menuItem') }}</span>
                </button>
            </div>

            <!-- Enterprise Banner -->
            <div class="enterprise-banner">
                <div class="enterprise-banner-content">
                    <div class="enterprise-banner-icon">
                        <b-icon icon="building" font-scale="2"></b-icon>
                    </div>
                    <div class="enterprise-banner-text">
                        <h4 class="enterprise-banner-title">{{ $t('marketplace.enterprise.title') }}</h4>
                        <p class="enterprise-banner-description">{{ $t('marketplace.enterprise.description') }}</p>
                    </div>
                </div>
                <a
                    :href="enterpriseMailtoLink"
                    class="btn enterprise-banner-btn"
                >
                    <b-icon icon="envelope" class="mr-2"></b-icon>
                    {{ $t('marketplace.enterprise.contactUs') }}
                </a>
            </div>

        <!-- Loading State -->
            <div v-if="loading" class="loading-state">
                <b-spinner variant="primary" label="Loading..." />
                <p class="loading-text mt-3">Loading marketplace items...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredItems.length === 0" class="empty-state">
                <div class="empty-icon">
                    <b-icon icon="inbox" font-scale="4"></b-icon>
                </div>
                <h3 class="empty-title">{{ $t('marketplace.noResults') }}</h3>
                <p class="empty-subtitle">Try adjusting your filters or search terms</p>
            </div>

            <!-- Items Grid -->
            <div v-else class="marketplace-grid">
                <div 
                    v-for="item in filteredItems" 
                    :key="item.id" 
                    class="marketplace-card"
                    :class="{ 'selected': isSelected(item) }"
                    @click="selectItem(item)"
                >
                    <!-- Discount Badge Overlay -->
                    <div class="discount-badge" v-if="hasAnyDiscount(item)">
                        <span>{{ getDiscountBadgeText(item) }}</span>
                    </div>
                    
                    <!-- Template with cover image URL -->
                    <div class="card-image" v-if="item.type === 'template' && item.cover && item.cover.startsWith('image:')">
                        <img :src="item.cover.replace('image:', '')" :alt="$translateLabel(item.label || item.name)" />
                    </div>
                    <!-- Template with b-icon -->
                    <div class="card-image card-icon" v-else-if="item.type === 'template' && item.cover && item.cover.startsWith('b-icon:')">
                        <b-icon :icon="item.cover.replace('b-icon:', '')" font-scale="2.5"></b-icon>
                    </div>
                    <!-- Template with plain cover (backwards compatibility) -->
                    <div class="card-image" v-else-if="item.type === 'template' && item.cover">
                        <img :src="item.cover" :alt="$translateLabel(item.label || item.name)" />
                    </div>
                    <!-- Template with icon property -->
                    <div class="card-image card-icon" v-else-if="item.type === 'template' && item.icon">
                        <b-icon :icon="item.icon" font-scale="2.5"></b-icon>
                    </div>
                    <!-- Template fallback -->
                    <div class="card-image card-icon" v-else-if="item.type === 'template'">
                        <b-icon icon="file-earmark-text" font-scale="2.5"></b-icon>
                    </div>
                    <!-- Integration with icon image URL -->
                    <div class="card-image" v-else-if="item.type === 'integration' && item.icon && item.icon.startsWith('image:')">
                        <img :src="item.icon.replace('image:', '')" :alt="$translateLabel(item.label || item.name)" />
                    </div>
                    <!-- Integration with b-icon -->
                    <div class="card-image card-icon" v-else-if="item.type === 'integration' && item.icon && item.icon.startsWith('b-icon:')">
                        <b-icon :icon="item.icon.replace('b-icon:', '')" font-scale="2.5"></b-icon>
                    </div>
                    <!-- Integration with icon component (backwards compatibility) -->
                    <div class="card-image card-icon" v-else-if="item.type === 'integration'">
                        <IntegrationIcon :icon="item.icon" />
                    </div>
                    <!-- Integration with cover (fallback) -->
                    <div class="card-image" v-else-if="item.type === 'integration' && item.cover">
                        <img :src="item.cover" :alt="$translateLabel(item.label || item.name)" />
                    </div>
                    <!-- Other items with cover -->
                    <div class="card-image" v-else-if="item.cover">
                        <img :src="item.cover" :alt="$translateLabel(item.label || item.name)" />
                    </div>
                    <!-- App with icon -->
                    <div class="card-image" v-else-if="item.type === 'app' && item.icon">
                        <img :src="item.icon" :alt="$translateLabel(item.label || item.name)" />
                    </div>
                    <!-- App fallback -->
                    <div class="card-image card-icon" v-else-if="item.type === 'app'">
                        <b-icon icon="grid-3x3-gap" font-scale="2.5"></b-icon>
                    </div>
                    <!-- Generic fallback -->
                    <div class="card-image card-icon" v-else>
                        <b-icon icon="box" font-scale="2.5"></b-icon>
                    </div>
                    
                    <div class="card-content">
                        <h3>
                            {{ $translateLabel(item.label || item.name) }}
                        </h3>
                        
                        <p class="card-description" v-if="item.description || item.short_description">
                            {{ $translateLabel(item.short_description || item.description) }}
                        </p>
                        
                        <div class="card-price" v-if="getPrice(item) !== null">
                            <template v-if="getPrice(item) == 0">
                                <span class="price-amount">{{ $t('marketplace.free') }}</span>
                                <b-badge 
                                    v-if="hasMultipleOptions(item)" 
                                    variant="info" 
                                    class="ml-1" 
                                    style="font-size: 0.75em;"
                                >
                                    +
                                </b-badge>
                            </template>
                            <template v-else>
                                <span class="price-amount" v-html="formatPrice(item)"></span>
                                <b-badge 
                                    v-if="hasMultipleOptions(item)" 
                                    variant="info" 
                                    class="ml-1" 
                                    style="font-size: 0.75em;"
                                >
                                    +
                                </b-badge>
                                <span class="price-period">{{ $t('marketplace.perMonth') }}</span>
                            </template>
                        </div>
                        
                        <!-- Usage Pricing from API -->
                        <div class="card-additional-units" v-if="getUsagePrices(item).length > 0 && account && !account.flat_rate">
                            <div v-for="(usagePrice, index) in getUsagePrices(item)" :key="index" class="unit-item">
                                <span class="unit-label">+ {{ $translateLabel(usagePrice.label) }}:</span>
                                <span class="unit-price">{{ formatUsagePrice(usagePrice.price) }}</span>
                            </div>
                        </div>
                        
                        <!-- Includes Count -->
                        <div class="card-includes" v-if="item.type === 'integration' && item.includes && item.includes.length > 0">
                            <b-icon icon="check-circle-fill" class="mr-1"></b-icon>
                            <span>{{ $t('marketplace.includes', { count: item.includes.length }) }}</span>
                        </div>
                    
                        <div class="card-badges">
                            <span 
                                class="badge badge-type"
                                :class="{
                                    'badge-integration': item.type === 'integration',
                                    'badge-template': item.type === 'template',
                                    'badge-app': item.type === 'app'
                                }"
                            >
                                {{ $t(`marketplace.${item.type}`) }}
                            </span>
                            
                            <span v-if="isExpired(item)" class="badge badge-expired">
                                <b-icon icon="exclamation-triangle-fill" scale="0.8"></b-icon>
                                {{ $t('marketplace.expired') }}
                            </span>
                            
                            <span v-else-if="isOwned(item)" class="badge badge-owned">
                                <b-icon icon="check-circle-fill" scale="0.8"></b-icon>
                                {{ $t('marketplace.owned') }}
                            </span>
                            
                            <span v-if="isTrial(item)" class="badge badge-trial">
                                {{ $t('marketplace.trial') }}
                            </span>
                            
                            <span v-if="item.beta" class="badge badge-beta">
                                beta
                            </span>
                            
                            <span v-if="isActive(item)" class="badge badge-active">
                                {{ $t('marketplace.active') }}
                            </span>
                            
                            <span v-if="isFree(item)" class="badge badge-free">
                                {{ $t('marketplace.free') }}
                            </span>
                            
                            <span v-if="hasOnboarding(item)" class="badge badge-onboarding">
                                <b-icon icon="clipboard-check" scale="0.8"></b-icon>
                                {{ $t('marketplace.hasOnboarding') }}
                            </span>
                            
                            <span 
                                v-if="isProrated(item) && !isOwned(item)"
                                class="badge badge-prorated"
                                style="cursor: help;"
                                :id="`prorated-badge-${item.id}`"
                            >
                                {{ $t('checkout.prorated') }}
                            </span>
                            <b-tooltip 
                                :target="`prorated-badge-${item.id}`"
                                :disabled="!(isProrated(item) && !isOwned(item))"
                                triggers="hover"
                                :delay="{ show: 100, hide: 300 }"
                                boundary="window"
                            >
                                {{ $t('checkout.proratedTooltip') }}
                            </b-tooltip>
                            
                            <span
                                v-if="hasMultipleProducts(item)"
                                class="badge badge-multi-product"
                                style="cursor: help;"
                                :id="`multi-product-badge-${item.id}`"
                            >
                                {{ $t('marketplace.includesProducts', { count: getPriceProducts(item).length }) }}
                            </span>
                            <b-tooltip
                                :target="`multi-product-badge-${item.id}`"
                                :disabled="!hasMultipleProducts(item)"
                                triggers="hover"
                                :delay="{ show: 100, hide: 300 }"
                                boundary="window"
                                placement="top"
                            >
                                <div v-for="p in getPriceProducts(item)" :key="p.id" class="text-left">
                                    {{ p.name }}
                                </div>
                            </b-tooltip>
                            
                            <span v-if="item.additional_charges" class="badge badge-additional-charges">
                                <b-icon icon="exclamation-circle" scale="0.8"></b-icon>
                                {{ $t('marketplace.additionalCharges') }}
                            </span>
                            
                            <span v-for="tag in (item.tags || []).slice(0, 3)" :key="tag" class="badge badge-tag">
                                {{ tag }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Integration Details Modal -->
        <main-dialog
            v-if="selectedItem && selectedItem.type === 'integration'"
            :show="showSidebar"
            :title="$translateLabel(selectedItem.label)"
            max-width="800px"
            @close="showSidebar = false"
        >
                <div class="px-3 py-2">
                    <!-- Icon with image: prefix as cover -->
                    <div class="integration-cover" v-if="selectedItem.icon && selectedItem.icon.startsWith('image:')">
                        <img :src="selectedItem.icon.replace('image:', '')" :alt="$translateLabel(selectedItem.label)" />
                    </div>
                    <!-- Icon with b-icon: prefix as cover -->
                    <div class="integration-cover integration-cover-icon" v-else-if="selectedItem.icon && selectedItem.icon.startsWith('b-icon:')">
                        <b-icon :icon="selectedItem.icon.replace('b-icon:', '')" font-scale="5"></b-icon>
                    </div>
                    <!-- Icon component as cover (backwards compatibility) -->
                    <div class="integration-cover integration-cover-icon" v-else-if="selectedItem.icon">
                        <IntegrationIcon :icon="selectedItem.icon" />
                    </div>
                    <!-- Cover fallback -->
                    <div class="integration-cover" v-else-if="selectedItem.cover">
                        <img :src="selectedItem.cover" :alt="$translateLabel(selectedItem.label)" />
                    </div>
                    <!-- Default fallback -->
                    <div class="integration-cover integration-cover-icon" v-else>
                        <b-icon icon="plug" font-scale="5"></b-icon>
                    </div>
                    
                    <!-- Includes Section -->
                    <div class="integration-includes mb-4" v-if="selectedItem.includes && selectedItem.includes.length > 0">
                        <h5>{{ $t('includes') }}</h5>
                        <ul class="includes-list">
                            <li v-for="(include, index) in selectedItem.includes" :key="index">
                                <b-icon icon="check-circle-fill" class="include-icon"></b-icon>
                                <span>{{ include[$store.getters.getLocale] || include['en_US'] || '' }}</span>
                            </li>
                        </ul>
                    </div>
                    
                    <!-- Option Set Selector -->
                    <div class="mb-4" v-if="selectedItem.option_set && selectedItem.option_set.items && selectedItem.option_set.items.length > 1">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h5 class="mb-0">{{ $t('marketplace.pricingOptions') }}</h5>
                            <b-spinner v-if="loadingOptionPrices" small variant="primary" label="Loading prices..."></b-spinner>
                        </div>
                        <div class="row">
                            <div 
                                v-for="option in selectedItem.option_set.items" 
                                :key="option.code"
                                class="col-12 col-md-4 mb-3"
                            >
                                <div 
                                    class="pricing-option-card" 
                                    :class="{ 
                                        'pricing-option-default': option.default,
                                        'pricing-option-current': isCurrentOption(selectedItem, option),
                                        'pricing-option-expanded': expandedOption === option.code,
                                        'pricing-option-selected': selectedOptionCode === option.code,
                                        'pricing-option-disabled': isOptionDisabled(selectedItem, option)
                                    }"
                                    @click="toggleExpandOption(selectedItem, option)"
                                >
                                    <div class="pricing-option-header">
                                        <div class="d-flex justify-content-between align-items-start">
                                            <div>
                                                <h6 class="mb-1">{{ $translateLabel(option.label) }}</h6>
                                                <div>
                                                    <b-badge v-if="option.default" variant="success" class="mb-2 mr-1">{{ $t('marketplace.default') }}</b-badge>
                                                    <b-badge v-if="isCurrentOption(selectedItem, option)" variant="primary" class="mb-2 mr-1">{{ $t('marketplace.currentPlan') }}</b-badge>
                                                    <b-badge v-else-if="isOptionDisabled(selectedItem, option)" variant="secondary" class="mb-2 mr-1">{{ $t('marketplace.unavailable') }}</b-badge>
                                                    <b-badge v-if="selectedOptionCode === option.code && !isCurrentOption(selectedItem, option) && !isOptionDisabled(selectedItem, option)" variant="info" class="mb-2">{{ $t('marketplace.selected') }}</b-badge>
                                                </div>
                                            </div>
                                            <b-icon 
                                                :icon="expandedOption === option.code ? 'chevron-up' : 'chevron-down'"
                                                font-scale="1.2"
                                                class="expand-icon"
                                            ></b-icon>
                                        </div>
                                    </div>
                                    <div class="pricing-option-price">
                                        <template v-if="getOptionPrice(option) === 0">
                                            <span class="price-lg">{{ $t('marketplace.free') }}</span>
                                        </template>
                                        <template v-else>
                                            <span class="price-lg" v-html="formatOptionPrice(option)"></span>
                                            <span class="price-period-sm">{{ $t('marketplace.perMonth') }}</span>
                                            <div v-if="isOptionProrated(option)" class="mt-1">
                                                <b-badge 
                                                    variant="info" 
                                                    style="cursor: help;"
                                                    v-b-tooltip.hover="{ delay: { show: 100, hide: 1000 } }"
                                                    :title="$t('checkout.proratedTooltip')"
                                                >
                                                    {{ $t('checkout.prorated') }}
                                                </b-badge>
                                            </div>
                                        </template>
                                    </div>
                                    <p class="pricing-option-description text-muted small">{{ $translateLabel(option.description) }}</p>
                                    <div v-if="option.policies && option.policies.length > 0" class="pricing-option-policies">
                                        <div 
                                            v-for="(policy) in (expandedOption === option.code ? option.policies : option.policies.slice(0, 3))" 
                                            :key="policy.name"
                                            class="policy-item"
                                        >
                                            <small class="text-muted">{{ getPolicyLabel(policy) }}:</small>
                                            <small class="font-weight-bold ml-1">{{ formatPolicyValue(policy) }}</small>
                                        </div>
                                        <small v-if="option.policies.length > 3 && expandedOption !== option.code" class="text-muted text-clickable">
                                            <b-icon icon="plus-circle" font-scale="0.9" class="mr-1"></b-icon>
                                            {{ $t('marketplace.clickToSeeAll') }} ({{ option.policies.length }} {{ $t('marketplace.total') }})
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Prorated Pricing Explanation -->
                        <div v-if="hasAnyOptionProrated(selectedItem)" class="alert alert-info mt-3 mb-3">
                            <div class="d-flex align-items-start">
                                <b-icon icon="info-circle-fill" class="mr-2 mt-1" font-scale="1.2"></b-icon>
                                <div>
                                    <strong>{{ $t('checkout.prorated') }}</strong>
                                    <p class="mb-0">{{ $t('checkout.proratedExplanation') }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Integration Info -->
                    <div class="integration-info">
                        <div class="info-item" v-if="selectedItem.id">
                            <label>{{ $t('marketplace.integrationId') }}</label>
                            <p>{{ selectedItem.id }}</p>
                        </div>
                        <div class="info-item" v-if="getPrice(selectedItem) !== null">
                            <label>{{ $t('marketplace.price') }}</label>
                            <p class="price-display">
                                <template v-if="getPrice(selectedItem) == 0">
                                    <span>{{ $t('marketplace.free') }}</span>
                                </template>
                                <template v-else>
                                    <span v-html="formatPrice(selectedItem)"></span>
                                    <b-badge 
                                        v-if="isProrated(selectedItem)" 
                                        variant="info" 
                                        class="ml-2" 
                                        style="cursor: help;"
                                        v-b-tooltip.hover="{ delay: { show: 100, hide: 1000 } }"
                                        :title="$t('checkout.proratedTooltip')"
                                    >
                                        {{ $t('checkout.prorated') }}
                                    </b-badge>
                                    <b-badge 
                                        v-if="hasAnyDiscount(selectedItem)" 
                                        variant="success" 
                                        class="ml-2"
                                    >
                                        {{ $t('marketplace.discountApplied') }}
                                    </b-badge>
                                    <br>
                                    <span class="price-period-small">{{ $t('marketplace.perMonth') }}</span>
                                </template>
                            </p>
                        </div>
                        <div class="info-item" v-if="hasMultipleProducts(selectedItem)">
                            <label>{{ $t('marketplace.priceBreakdown') }}</label>
                            <div>
                                <div v-for="p in getPriceProducts(selectedItem)" :key="p.id" class="d-flex justify-content-between align-items-center mb-1" style="font-size: 0.9em;">
                                    <span>
                                        <b-badge variant="secondary" class="mr-1" style="font-size: 0.75em;">{{ p.group }}</b-badge>
                                        {{ p.name }}
                                    </span>
                                    <span class="ml-3 text-nowrap">{{ formatProductPrice(p) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="info-item" v-if="getProductDiscounts(selectedItem).length > 0">
                            <label>{{ $t('marketplace.discount') }}</label>
                            <div>
                                <div v-for="(pd, idx) in getProductDiscounts(selectedItem)" :key="idx" class="mb-2">
                                    <div class="d-flex align-items-center mb-1">
                                        <b-badge variant="success" class="mr-2">{{ pd.discount.name }}</b-badge>
                                        <span class="text-muted" style="font-size: 0.9em;">{{ pd.product_name }}</span>
                                    </div>
                                    <div style="font-size: 0.9em;">
                                        <span class="text-muted" style="text-decoration: line-through;">{{ formatDiscountPrice(pd.original_price) }}</span>
                                        <b-icon icon="arrow-right" class="mx-1" style="font-size: 0.8em;"></b-icon>
                                        <strong class="text-success">{{ formatDiscountPrice(pd.discounted_price) }}</strong>
                                        <span class="ml-2 text-success">(-{{ formatDiscount(pd.discount) }})</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="info-item" v-if="selectedItem.tags && selectedItem.tags.length > 0">
                            <label>{{ $t('marketplace.tags') }}</label>
                            <div class="d-flex flex-wrap gap-2">
                                <b-badge v-for="tag in selectedItem.tags" :key="tag" variant="secondary" class="mr-1">{{ tag }}</b-badge>
                            </div>
                        </div>
                        <div class="info-item" v-if="selectedItem.categories && selectedItem.categories.length > 0">
                            <label>{{ $t('marketplace.categories') }}</label>
                            <div class="d-flex flex-wrap gap-2">
                                <b-badge v-for="category in selectedItem.categories" :key="category" variant="info" class="mr-1">{{ category }}</b-badge>
                            </div>
                        </div>
                        <div class="info-item" v-if="isActive(selectedItem)">
                            <b-badge variant="success" pill>
                                <b-icon icon="check-circle"></b-icon> {{ $t('marketplace.active') }}
                            </b-badge>
                        </div>
                        <div class="info-item" v-else-if="canActivate(selectedItem)">
                            <b-badge variant="info" pill>
                                <b-icon icon="circle"></b-icon> {{ $t('marketplace.available') }}
                            </b-badge>
                        </div>
                        <div class="info-item" v-else>
                            <b-badge variant="warning" pill>
                                <b-icon icon="lock"></b-icon> {{ $t('marketplace.requiresPurchase') }}
                            </b-badge>
                        </div>
                        <div class="info-item" v-if="selectedItem.additional_charges">
                            <b-badge variant="warning" pill>
                                <b-icon icon="exclamation-circle"></b-icon> {{ $t('marketplace.additionalCharges') }}
                            </b-badge>
                            <p class="additional-charges-note">{{ $t('marketplace.additionalChargesNote') }}</p>
                        </div>
                        <div class="info-item" v-if="getUsagePrices(selectedItem).length > 0 && account && !account.flat_rate">
                            <label>{{ $t('marketplace.usagePricing') }}</label>
                            <div class="additional-units-list">
                                <div v-for="(usagePrice, index) in getUsagePrices(selectedItem)" :key="index" class="unit-detail">
                                    <div class="unit-name">{{ $translateLabel(usagePrice.label) }}</div>
                                    <div class="unit-pricing">
                                        {{ formatUsagePrice(usagePrice.price) }} {{ $t('marketplace.perUnit') }}
                                        <span v-if="usagePrice.unit" class="unit-type">({{ usagePrice.unit }})</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                </div>
                <template #footer>
                    <div class="w-100 d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center gap-2">
                            <b-button 
                                v-if="selectedItem.docs"
                                variant="outline-secondary"
                                :href="docsUrl + selectedItem.docs"
                                target="_blank"
                            >
                                <b-icon icon="book" class="mr-1"></b-icon>
                                {{ $t('marketplace.documentation') }}
                            </b-button>
                            <template v-if="isActive(selectedItem)">
                                <b-button 
                                    variant="primary" 
                                    v-if="hasIntegrationSettings(selectedItem)"
                                    @click="goToIntegrationSettings"
                                >
                                    {{ $t('marketplace.settings') }}
                                </b-button>
                                <b-button 
                                    variant="warning" 
                                    @click="deactivateIntegration"
                                >
                                    {{ $t('marketplace.deactivate') }}
                                </b-button>
                            </template>
                        </div>
                        <div class="d-flex align-items-center gap-2">
                            <protokol-checkout-button
                                v-if="isTrial(selectedItem)"
                                productType="integration"
                                :reference="selectedItem.id"
                                size="lg"
                                class="checkout-button-prominent"
                                :title="$t('marketplace.buy')"
                            />
                            <template v-if="isActive(selectedItem)">
                                <protokol-checkout-button 
                                    v-if="shouldShowCheckoutForOption(selectedItem)"
                                    productType="integration" 
                                    :reference="selectedItem.id"
                                    :options="getCheckoutOptions(selectedItem)"
                                    size="lg"
                                    class="checkout-button-prominent"
                                    :title="$t('marketplace.upgrade')"
                                />
                            </template>
                            <b-button 
                                variant="success" 
                                @click="integrateIntegration"
                                v-else-if="canIntegrate(selectedItem)"
                            >
                                <b-icon icon="plug" class="mr-1"></b-icon> {{ $t('marketplace.integrate') }}
                            </b-button>
                            <protokol-checkout-button 
                                v-else-if="shouldShowCheckoutForOption(selectedItem)"
                                productType="integration" 
                                :reference="selectedItem.id"
                                :options="getCheckoutOptions(selectedItem)"
                                size="lg"
                                class="checkout-button-prominent"
                            />
                            <b-button 
                                variant="primary" 
                                @click="activateIntegration"
                                v-else-if="canActivate(selectedItem)"
                            >
                                {{ $t('marketplace.activate') }}
                            </b-button>
                            <protokol-checkout-button 
                                v-else-if="isExpired(selectedItem)"
                                productType="integration" 
                                :reference="selectedItem.id"
                                size="lg"
                                class="checkout-button-prominent"
                            >
                                <template #default>
                                    <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
                                    {{ $t('marketplace.renew') }}
                                </template>
                            </protokol-checkout-button>
                            <protokol-checkout-button 
                                v-else
                                productType="integration" 
                                :reference="selectedItem.id"
                                size="lg"
                                class="checkout-button-prominent"
                            />
                        </div>
                    </div>
                </template>
        </main-dialog>

        <!-- App Details Modal -->
        <main-dialog
            v-if="selectedItem && selectedItem.type === 'app'"
            :show="showSidebar"
            :title="selectedItem.name"
            :icon="selectedItem.icon || null"
            max-width="800px"
            @close="showSidebar = false"
        >
                <div class="px-3 py-2">
                    
                    <!-- Description -->
                    <div class="integration-description mb-4">
                        <p v-if="getAppDescription(selectedItem)">{{ getAppDescription(selectedItem) }}</p>
                        <p v-else class="text-muted">{{ $t('marketplace.noDescription') }}</p>
                    </div>
                    
                    <!-- App Info -->
                    <div class="integration-info">
                        <div class="info-item" v-if="selectedItem.uuid">
                            <label>{{ $t('marketplace.appId') }}</label>
                            <p>{{ selectedItem.uuid }}</p>
                        </div>
                        <div class="info-item" v-if="getPrice(selectedItem) !== null">
                            <label>{{ $t('marketplace.price') }}</label>
                            <p class="price-display">
                                <template v-if="getPrice(selectedItem) == 0">
                                    <span>{{ $t('marketplace.free') }}</span>
                                </template>
                                <template v-else>
                                    <span v-html="formatPrice(selectedItem)"></span>
                                    <b-badge 
                                        v-if="isProrated(selectedItem)" 
                                        variant="info" 
                                        class="ml-2" 
                                        style="cursor: help;"
                                        v-b-tooltip.hover="{ delay: { show: 100, hide: 5000 } }"
                                        :title="$t('checkout.proratedTooltip')"
                                    >
                                        {{ $t('checkout.prorated') }}
                                    </b-badge>
                                    <b-badge 
                                        v-if="hasAnyDiscount(selectedItem)" 
                                        variant="success" 
                                        class="ml-2"
                                    >
                                        {{ $t('marketplace.discountApplied') }}
                                    </b-badge>
                                    <br>
                                    <span class="price-period-small">{{ $t('marketplace.perMonth') }}</span>
                                </template>
                            </p>
                        </div>
                        <div class="info-item" v-if="hasMultipleProducts(selectedItem)">
                            <label>{{ $t('marketplace.priceBreakdown') }}</label>
                            <div>
                                <div v-for="p in getPriceProducts(selectedItem)" :key="p.id" class="d-flex justify-content-between align-items-center mb-1" style="font-size: 0.9em;">
                                    <span>
                                        <b-badge variant="secondary" class="mr-1" style="font-size: 0.75em;">{{ p.group }}</b-badge>
                                        {{ p.name }}
                                    </span>
                                    <span class="ml-3 text-nowrap">{{ formatProductPrice(p) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="info-item" v-if="getProductDiscounts(selectedItem).length > 0">
                            <label>{{ $t('marketplace.discount') }}</label>
                            <div>
                                <div v-for="(pd, idx) in getProductDiscounts(selectedItem)" :key="idx" class="mb-2">
                                    <div class="d-flex align-items-center mb-1">
                                        <b-badge variant="success" class="mr-2">{{ pd.discount.name }}</b-badge>
                                        <span class="text-muted" style="font-size: 0.9em;">{{ pd.product_name }}</span>
                                    </div>
                                    <div style="font-size: 0.9em;">
                                        <span class="text-muted" style="text-decoration: line-through;">{{ formatDiscountPrice(pd.original_price) }}</span>
                                        <b-icon icon="arrow-right" class="mx-1" style="font-size: 0.8em;"></b-icon>
                                        <strong class="text-success">{{ formatDiscountPrice(pd.discounted_price) }}</strong>
                                        <span class="ml-2 text-success">(-{{ formatDiscount(pd.discount) }})</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="info-item" v-if="selectedItem.tags && selectedItem.tags.length > 0">
                            <label>{{ $t('marketplace.tags') }}</label>
                            <div class="d-flex flex-wrap gap-2">
                                <b-badge v-for="tag in selectedItem.tags" :key="tag" variant="secondary" class="mr-1 mb-1">{{ tag }}</b-badge>
                            </div>
                        </div>
                        <div class="info-item" v-if="selectedItem.categories && selectedItem.categories.length > 0">
                            <label>{{ $t('marketplace.categories') }}</label>
                            <div class="d-flex flex-wrap gap-2">
                                <b-badge v-for="category in selectedItem.categories" :key="category" variant="info" class="mr-1 mb-1">{{ category }}</b-badge>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Disclaimer for template-owned apps -->
                    <b-alert 
                        v-if="isAppOwnedThroughTemplate(selectedItem)"
                        variant="info" 
                        show 
                        class="mt-4 mb-0"
                    >
                        <div class="d-flex align-items-start">
                            <b-icon icon="info-circle-fill" class="mr-2 mt-1"></b-icon>
                            <div>
                                <strong>{{ $t('marketplace.ownedThroughTemplate') }}</strong>
                                <p class="mb-0 mt-1" style="font-size: 0.9rem;">
                                    {{ $t('marketplace.ownedThroughTemplateDescription') }}
                                </p>
                            </div>
                        </div>
                    </b-alert>

                    <!-- Inline Preflight: Requirements & Permissions -->
                    <template v-if="isFree(selectedItem) && !isAppInstalled(selectedItem)">
                        <hr class="mp-divider" />

                        <div v-if="loadingAppPreflight" class="text-center py-3">
                            <b-spinner variant="primary" small></b-spinner>
                            <p class="text-muted mt-2 mb-0" style="font-size: 0.85rem;">{{ $t('marketplace.installWizard.checkingRequirements') }}</p>
                        </div>

                        <div v-else-if="appPreflightError" class="alert alert-danger mt-0 mb-0" style="font-size: 0.85rem;">
                            <b-icon icon="exclamation-circle-fill" class="mr-2"></b-icon>{{ appPreflightError }}
                        </div>

                        <template v-else-if="appPreflight">
                            <!-- Requirements -->
                            <div v-if="appPreflightHasRequirements" class="iw-section">
                                <div class="iw-section-title">
                                    <b-icon icon="check2-circle" class="mr-1"></b-icon>
                                    {{ $t('marketplace.installWizard.requirements') }}
                                </div>
                                <div
                                    v-for="req in appPreflight.integrations"
                                    :key="req.id"
                                    class="iw-req-row"
                                    :class="{ satisfied: req.satisfied, unsatisfied: !req.satisfied && !req.optional, 'optional-unsatisfied': !req.satisfied && req.optional }"
                                >
                                    <b-icon
                                        :icon="req.satisfied ? 'check-circle-fill' : (req.optional ? 'dash-circle' : 'x-circle-fill')"
                                        :class="req.satisfied ? 'text-success' : (!req.optional ? 'text-danger' : '')"
                                        class="iw-req-icon"
                                    ></b-icon>
                                    <div class="iw-req-details">
                                        <span class="iw-req-name">{{ getIntegrationLabel(req.id) }}</span>
                                        <b-badge v-if="req.optional" variant="secondary" class="ml-1 iw-badge">{{ $t('marketplace.installWizard.optional') }}</b-badge>
                                        <b-badge v-if="!req.satisfied && !req.optional" variant="danger" class="ml-1 iw-badge">{{ $t('marketplace.installWizard.required') }}</b-badge>
                                        <small class="d-block text-muted">{{ $t('marketplace.installWizard.integration') }}</small>
                                    </div>
                                    <button
                                        v-if="!req.satisfied && !req.optional"
                                        class="iw-btn iw-btn-install ml-auto"
                                        @click="installIntegrationFromPreflight(req)"
                                    >
                                        <b-icon icon="box-arrow-up-right" class="mr-1"></b-icon>
                                        {{ $t('marketplace.installWizard.installIntegration') }}
                                    </button>
                                </div>
                                <div
                                    v-for="req in appPreflight.components"
                                    :key="req.ref"
                                    class="iw-req-row"
                                    :class="{ satisfied: req.satisfied, unsatisfied: !req.satisfied }"
                                >
                                    <b-icon
                                        :icon="req.satisfied ? 'check-circle-fill' : 'x-circle-fill'"
                                        :class="req.satisfied ? 'text-success' : 'text-danger'"
                                        class="iw-req-icon"
                                    ></b-icon>
                                    <div class="iw-req-details">
                                        <span class="iw-req-name">{{ req.ref }}</span>
                                        <b-badge v-if="!req.satisfied" variant="danger" class="ml-1 iw-badge">{{ $t('marketplace.installWizard.required') }}</b-badge>
                                        <small v-if="req.reason" class="d-block text-muted">{{ req.reason }}</small>
                                        <small v-else class="d-block text-muted">{{ $t('marketplace.installWizard.component') }}</small>
                                    </div>
                                </div>
                                <div v-if="!appPreflight.ready" class="alert alert-warning mt-3 mb-0" style="font-size: 0.85rem;">
                                    <div class="d-flex align-items-center justify-content-between">
                                        <span>
                                            <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon>{{ $t('marketplace.installWizard.unmetRequirements') }}
                                        </span>
                                        <button
                                            class="iw-btn iw-btn-secondary iw-btn-sm ml-3"
                                            :disabled="loadingAppPreflight"
                                            @click="loadAppPreflight(selectedItem)"
                                        >
                                            <b-spinner v-if="loadingAppPreflight" small class="mr-1"></b-spinner>
                                            <b-icon v-else icon="arrow-clockwise" class="mr-1"></b-icon>
                                            {{ $t('marketplace.installWizard.refreshRequirements') }}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Install Permissions (collapsible) -->
                            <div v-if="appPreflight.install_permissions && appPreflight.install_permissions.length > 0" class="iw-section">
                                <button
                                    class="iw-expand-btn"
                                    @click="appInstallPermsExpanded = !appInstallPermsExpanded"
                                >
                                    <b-icon icon="shield-lock" class="mr-1"></b-icon>
                                    {{ $t('marketplace.installWizard.installPermissions') }}
                                    <span class="iw-expand-count">{{ appPreflight.install_permissions.length }}</span>
                                    <b-icon :icon="appInstallPermsExpanded ? 'chevron-up' : 'chevron-down'" class="ml-auto"></b-icon>
                                </button>
                                <div v-if="appInstallPermsExpanded">
                                    <p class="text-muted small mb-2 mt-2">{{ $t('marketplace.installWizard.installPermissionsDescription') }}</p>
                                    <div
                                        v-for="(perm, idx) in appPreflight.install_permissions"
                                        :key="idx"
                                        class="iw-perm-row"
                                    >
                                        <b-icon
                                            icon="key-fill"
                                            class="iw-perm-icon text-muted"
                                        ></b-icon>
                                        <div>
                                            <code class="iw-perm-name">{{ typeof perm === 'string' ? perm : perm.permission }}</code>
                                            <small v-if="typeof perm !== 'string' && perm.reason" class="d-block text-muted mt-1">{{ perm.reason }}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- No requirements message -->
                            <div
                                v-if="!appPreflightHasRequirements && (!appPreflight.install_permissions || appPreflight.install_permissions.length === 0)"
                                class="text-center py-2"
                            >
                                <b-icon icon="check-circle-fill" variant="success" font-scale="1.5"></b-icon>
                                <p class="mt-2 mb-0 text-muted small">{{ $t('marketplace.installWizard.noRequirements') }}</p>
                            </div>
                        </template>
                    </template>

                    <!-- Runtime Permissions (from app detail, collapsible) -->
                    <template v-if="isFree(selectedItem) && !isAppInstalled(selectedItem)">
                        <div v-if="appDetail && appDetail.runtime_permissions && appDetail.runtime_permissions.length > 0" class="iw-section mt-2">
                            <button
                                class="iw-expand-btn"
                                @click="appRuntimePermsExpanded = !appRuntimePermsExpanded"
                            >
                                <b-icon icon="cpu" class="mr-1"></b-icon>
                                {{ $t('marketplace.installWizard.runtimePermissions') }}
                                <span class="iw-expand-count">{{ appDetail.runtime_permissions.length }}</span>
                                <b-icon :icon="appRuntimePermsExpanded ? 'chevron-up' : 'chevron-down'" class="ml-auto"></b-icon>
                            </button>
                            <div v-if="appRuntimePermsExpanded">
                                <p class="text-muted small mb-2 mt-2">{{ $t('marketplace.installWizard.runtimePermissionsDescription') }}</p>
                                <div
                                    v-for="(perm, idx) in appDetail.runtime_permissions"
                                    :key="idx"
                                    class="iw-perm-row"
                                >
                                    <b-icon icon="lightning-fill" class="iw-perm-icon text-muted"></b-icon>
                                    <code class="iw-perm-name">{{ perm }}</code>
                                </div>
                            </div>
                        </div>

                        <!-- Exposed Permissions (collapsible) -->
                        <div v-if="appDetail && appDetail.permissions && appDetail.permissions.length > 0" class="iw-section mt-2">
                            <button
                                class="iw-expand-btn"
                                @click="appExposedPermsExpanded = !appExposedPermsExpanded"
                            >
                                <b-icon icon="people" class="mr-1"></b-icon>
                                {{ $t('marketplace.installWizard.exposedPermissions') }}
                                <span class="iw-expand-count">{{ appDetail.permissions.length }}</span>
                                <b-icon :icon="appExposedPermsExpanded ? 'chevron-up' : 'chevron-down'" class="ml-auto"></b-icon>
                            </button>
                            <div v-if="appExposedPermsExpanded">
                                <p class="text-muted small mb-2 mt-2">{{ $t('marketplace.installWizard.exposedPermissionsDescription') }}</p>
                                <div
                                    v-for="(perm, idx) in appDetail.permissions"
                                    :key="idx"
                                    class="iw-perm-row"
                                >
                                    <b-icon icon="person-badge" class="iw-perm-icon text-muted"></b-icon>
                                    <div>
                                        <code class="iw-perm-name">{{ perm.permission }}</code>
                                        <small v-if="perm.label" class="d-block text-muted mt-1">{{ $translateLabel(perm.label) }}</small>
                                        <small v-if="perm.description" class="d-block text-muted">{{ $translateLabel(perm.description) }}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Workspace selection for install -->
                    <template v-if="isFree(selectedItem) && !isAppInstalled(selectedItem)">
                        <div class="iw-section mt-2">
                            <div class="iw-section-header d-flex align-items-center mb-2">
                                <b-icon icon="grid" class="mr-2 text-muted"></b-icon>
                                <span class="font-weight-600">{{ $t('marketplace.installWizard.selectWorkspaces') }}</span>
                            </div>
                            <p class="text-muted small mb-2">{{ $t('marketplace.installWizard.selectWorkspacesDescription') }}</p>
                            <div v-if="projectWorkspaceOptions.length > 0">
                                <b-form-checkbox
                                    v-for="ws in projectWorkspaceOptions"
                                    :key="ws.uuid"
                                    v-model="selectedInstallWorkspaces"
                                    :value="ws.uuid"
                                    class="mb-1"
                                >
                                    {{ ws.name }}
                                </b-form-checkbox>
                            </div>
                            <p v-else class="text-muted small">{{ $t('marketplace.installWizard.noWorkspacesFound') }}</p>
                            <small class="text-muted d-block mt-2">
                                <b-icon icon="info-circle" class="mr-1"></b-icon>
                                {{ $t('marketplace.installWizard.workspacesCanChange') }}
                            </small>
                        </div>
                    </template>
                    
                </div>
                <template #footer>
                    <div
                        v-if="!isAppOwnedThroughTemplate(selectedItem)"
                        class="w-100 d-flex align-items-center justify-content-between"
                    >
                        <protokol-checkout-button 
                            v-if="isExpired(selectedItem)"
                            productType="app" 
                            :reference="selectedItem.uuid"
                            size="lg"
                            class="checkout-button-prominent ml-auto"
                        >
                            <template #default>
                                <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
                                {{ $t('marketplace.renew') }}
                            </template>
                        </protokol-checkout-button>
                        <div
                            v-else-if="isOwned(selectedItem)"
                            class="d-flex align-items-center"
                            style="gap: 8px;"
                        >
                            <b-button
                                v-if="isUpdateAvailable(selectedItem)"
                                variant="warning"
                                :disabled="applyingUpdate"
                                @click="applyUpdate"
                            >
                                <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
                                {{ applyingUpdate ? $t('marketplace.updating') : $t('marketplace.update') }}
                            </b-button>
                            <b-button
                                variant="primary"
                                @click="goToAppSettings"
                            >
                                {{ $t('marketplace.settings') }}
                            </b-button>
                        </div>
                        <div 
                            v-else-if="isFree(selectedItem) && isAppInstalled(selectedItem)"
                            class="d-flex align-items-center"
                            style="gap: 8px;"
                        >
                            <b-button
                                v-if="isUpdateAvailable(selectedItem)"
                                variant="warning"
                                :disabled="applyingUpdate"
                                @click="applyUpdate"
                            >
                                <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
                                {{ applyingUpdate ? $t('marketplace.updating') : $t('marketplace.update') }}
                            </b-button>
                            <b-button
                                variant="outline-danger"
                                :disabled="uninstallingApp"
                                @click="doUninstallApp"
                            >
                                <b-spinner v-if="uninstallingApp" small class="mr-1"></b-spinner>
                                <b-icon v-else icon="trash" class="mr-1"></b-icon>
                                {{ $t('marketplace.uninstall') }}
                            </b-button>
                            <b-button 
                                variant="primary" 
                                @click="goToAppSettings"
                            >
                                {{ $t('marketplace.settings') }}
                            </b-button>
                        </div>
                        <b-button 
                            v-else-if="isFree(selectedItem)"
                            variant="primary" 
                            :disabled="installingApp || loadingAppPreflight || (appPreflight && !appPreflight.ready)"
                            @click="doInstallApp"
                        >
                            <b-spinner v-if="installingApp" small class="mr-1"></b-spinner>
                            <b-icon v-else icon="download" class="mr-1"></b-icon>
                            {{ $t('install') }}
                        </b-button>
                        <protokol-checkout-button 
                            v-else
                            productType="app" 
                            :reference="selectedItem.uuid"
                            size="lg"
                            class="checkout-button-prominent ml-auto"
                        />
                    </div>
                </template>
        </main-dialog>

        <!-- Template Details Modal -->
        <main-dialog
            v-if="selectedItem && selectedItem.type === 'template'"
            :show="showSidebar"
            :title="$translateLabel(selectedItem.label)"
            max-width="800px"
            @close="showSidebar = false"
        >
                <div class="px-3 py-2">
                    <template v-if="loadingDetails">
                        <b-skeleton width="100%" height="500px" animation="throb" />
                    </template>
                    <template v-else-if="selectedItemDetails">
                    
                        <TemplateDescription :item="selectedTemplateModel" type="single" />
                        
                        <!-- Template Info -->
                        <div class="integration-info mt-3">
                            <div class="info-item" v-if="selectedItem.tags && selectedItem.tags.length > 0">
                                <label>{{ $t('marketplace.tags') }}</label>
                                <div class="d-flex flex-wrap gap-2">
                                    <b-badge v-for="tag in selectedItem.tags" :key="tag" variant="secondary" class="mr-1 mb-1">{{ tag }}</b-badge>
                                </div>
                            </div>
                            <div class="info-item" v-if="selectedItem.categories && selectedItem.categories.length > 0">
                                <label>{{ $t('marketplace.categories') }}</label>
                                <div class="d-flex flex-wrap gap-2">
                                    <b-badge v-for="category in selectedItem.categories" :key="category" variant="info" class="mr-1 mb-1">{{ category }}</b-badge>
                                </div>
                            </div>
                        </div>
                    </template>
                    
                </div>
                <template #footer>
                    <div v-if="!loadingDetails" class="w-100 d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center gap-2">
                            <b-button 
                                @click="startTemplateTrial"
                                v-if="canTrialTemplate(selectedItem)"
                                class="trial-button"
                            >
                                <b-icon icon="lightning-fill" class="mr-1"></b-icon>
                                {{ $t('marketplace.startTrial') }}
                            </b-button>
                            <b-button 
                                variant="warning" 
                                @click="uninstallTemplate"
                                v-if="isTemplateInstalled(selectedItem)"
                            >
                                {{ $t('marketplace.uninstall') }}
                            </b-button>
                        </div>
                        <div class="d-flex align-items-center gap-2">
                            <b-button 
                                variant="primary" 
                                @click="installTemplate"
                                v-if="canInstallTemplate(selectedItem)"
                            >
                                {{ $t('install') }}
                            </b-button>
                            <protokol-checkout-button 
                                v-if="isExpired(selectedItem)"
                                productType="template" 
                                :reference="selectedItem.id.replace('template-', '')"
                                size="lg"
                                class="checkout-button-prominent"
                            >
                                <template #default>
                                    <b-icon icon="arrow-clockwise" class="mr-1"></b-icon>
                                    {{ $t('marketplace.renew') }}
                                </template>
                            </protokol-checkout-button>
                            <protokol-checkout-button 
                                v-else-if="canBuyTemplate(selectedItem)"
                                productType="template" 
                                :reference="selectedItem.id.replace('template-', '')"
                                size="lg"
                                class="checkout-button-prominent"
                            />
                        </div>
                    </div>
                </template>
        </main-dialog>

        <!-- Onboarding Required Modal -->
        <main-dialog
            :show="showOnboardingModal"
            :title="$t('marketplace.onboardingRequired')"
            max-width="500px"
            @close="cancelOnboarding"
        >
                <p class="onboarding-message mb-0">{{ onboardingMessage }}</p>
                <template #footer>
                    <b-button 
                        variant="outline-secondary" 
                        @click="cancelOnboarding"
                    >
                        {{ $t('cancel') }}
                    </b-button>
                    <b-button 
                        variant="primary" 
                        @click="startOnboarding"
                    >
                        {{ $t('marketplace.finishOnboarding') }}
                    </b-button>
                </template>
        </main-dialog>
    
    <!-- Confirmation Modal -->
    <ConfirmModal
        :show="confirmModal.show"
        :title="confirmModal.title"
        :message="confirmModal.message"
        :confirmText="confirmModal.confirmText"
        :dangerButton="confirmModal.danger"
        :icon="confirmModal.icon"
        @confirm="handleConfirmAction"
        @cancel="closeConfirmModal"
    />

    <RedeemCodeModal :show="showRedeemCodeModal" @close="showRedeemCodeModal = false" @redeemed="onCodeRedeemed" />

    </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/style.scss';
@import '@/assets/scss/_custom.scss';
@import '@/assets/scss/_custom.scss';

.marketplace-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    background: $dark-hard2-background;
    border-radius: 12px;
}

.integration-description {
    h5 {
        font-size: 14px;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    
    p {
        font-size: 14px;
        line-height: 1.6;
        color: var(--text-secondary);
    }
}

.integration-includes {
    h5 {
        font-size: 14px;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    
    .includes-list {
        list-style: none;
        padding: 0;
        margin: 0;
        
        li {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 10px 0;
            font-size: 14px;
            line-height: 1.6;
            color: var(--text-secondary);
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
            
            &:last-child {
                border-bottom: none;
            }
            
            .include-icon {
                color: #10b981;
                font-size: 18px;
                flex-shrink: 0;
                margin-top: 2px;
            }
            
            span {
                flex: 1;
            }
        }
    }
}

.pricing-option-card {
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.02);
    transition: all 0.2s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    user-select: none;
    
    &:hover {
        border-color: rgba(255, 255, 255, 0.2);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    &.pricing-option-default {
        border-color: #10b981;
        background: rgba(16, 185, 129, 0.05);
    }
    
    &.pricing-option-current {
        border-color: #3b82f6;
        background: rgba(59, 130, 246, 0.05);
    }
    
    &.pricing-option-expanded {
        border-color: rgba(255, 255, 255, 0.3);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        
        .expand-icon {
            color: var(--primary);
        }
    }
    
    &.pricing-option-selected {
        border-color: #6c80ff;
        background: rgba(102, 126, 234, 0.08);
        box-shadow: 0 4px 16px rgba(102, 126, 234, 0.25);
        
        &:hover {
            border-color: #7888ea;
            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.35);
        }
    }
    
    &.pricing-option-disabled {
        opacity: 0.5;
        cursor: not-allowed;
        
        &:hover {
            transform: none;
            box-shadow: none;
        }
    }
    
    .pricing-option-header {
        h6 {
            font-weight: 600;
            color: var(--text-primary);
            margin-bottom: 8px;
        }
        
        .expand-icon {
            color: var(--text-secondary);
            transition: all 0.2s ease;
            flex-shrink: 0;
        }
    }
    
    .pricing-option-price {
        margin: 12px 0;
        
        .price-lg {
            font-size: 28px;
            font-weight: 700;
            color: var(--text-primary);
        }
        
        .price-period-sm {
            font-size: 12px;
            color: var(--text-secondary);
            margin-left: 4px;
        }
    }
    
    .pricing-option-description {
        font-size: 13px;
        line-height: 1.5;
        margin-bottom: 12px;
        flex-grow: 1;
    }
    
    .pricing-option-policies {
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        padding-top: 12px;
        margin-top: auto;
        
        .text-clickable {
            display: block;
            margin-top: 8px;
            color: var(--primary);
            font-weight: 500;
            cursor: pointer;
            
            &:hover {
                text-decoration: underline;
            }
        }
        
        .policy-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 6px;
            font-size: 12px;
            
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}

.integration-info {
    .info-item {
        margin-bottom: 16px;
        
        label {
            font-size: 12px;
            font-weight: 600;
            color: var(--text-secondary);
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 4px;
            display: block;
        }
        
        p {
            font-size: 14px;
            color: rgba(255, 255, 255, 0.9);
            font-family: monospace;
            margin: 0;
        }
        
        .badge {
            font-size: 13px;
            padding: 8px 14px;
            font-weight: 500;
            display: inline-flex;
            align-items: center;
            gap: 6px;
        }
    }
    
    .additional-charges-note {
        font-size: 12px;
        color: var(--text-secondary);
        margin-top: 8px;
        font-style: italic;
    }
    
    .additional-units-list {
        margin-top: 8px;
        
        .unit-detail {
            background: rgba(245, 158, 11, 0.1);
            border: 1px solid rgba(245, 158, 11, 0.3);
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 8px;
            
            .unit-name {
                font-size: 13px;
                font-weight: 600;
                color: rgba(255, 255, 255, 0.9);
                margin-bottom: 4px;
            }
            
            .unit-pricing {
                font-size: 14px;
                font-weight: 600;
                color: rgba(245, 158, 11, 0.9);
                font-family: monospace;
            }
        }
    }
    
    .price-display {
        font-size: 1.3rem;
        font-weight: 600;
        color: $protokol-royal-active;
        
        .price-period-small {
            font-size: 0.75rem;
            color: var(--text-secondary);
            font-weight: normal;
            margin-left: 4px;
        }
    }
}

.integration-cover {
    width: 100%;
    height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $dark-hard2-background;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 20px;
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    
    &.integration-cover-icon {
        color: var(--text-secondary);
    }
}

.enterprise-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 20px 28px;
    border: 1px solid rgba(102, 126, 234, 0.35);
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%);
    margin-bottom: 28px;
    flex-wrap: wrap;

    .enterprise-banner-content {
        display: flex;
        align-items: center;
        gap: 18px;
    }

    .enterprise-banner-icon {
        color: #818cf8;
        flex-shrink: 0;
    }

    .enterprise-banner-text {
        .enterprise-banner-title {
            font-size: 16px;
            font-weight: 700;
            color: var(--text-primary);
            margin: 0 0 4px;
        }

        .enterprise-banner-description {
            font-size: 13px;
            color: var(--text-secondary);
            margin: 0;
        }
    }

    .enterprise-banner-btn {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        padding: 10px 22px;
        border: 1.5px solid rgba(102, 126, 234, 0.6);
        border-radius: 8px;
        background: rgba(102, 126, 234, 0.12);
        color: #818cf8;
        font-size: 14px;
        font-weight: 600;
        transition: all 0.2s ease;
        text-decoration: none;

        &:hover {
            background: rgba(102, 126, 234, 0.22);
            border-color: #818cf8;
            color: #a5b4fc;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
            text-decoration: none;
        }
    }
}

.marketplace-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0;
}

// Search Header
.marketplace-header {
    .search-wrapper {
        max-width: 600px;
        
        .search-icon {
            background: $dark-background;
            border: 1px solid $dark-border2;
            border-radius: 12px 0 0 12px;
            padding: 0 16px;
            color: var(--text-muted);
        }
        
        .search-input {
            background: $dark-background;
            border: 1px solid $dark-border2;
            border-left: none;
            border-radius: 0 12px 12px 0;
            padding: 14px 20px;
            font-size: 1rem;
            color: var(--text-primary);
            
            &::placeholder {
                color: rgba(255, 255, 255, 0.4);
            }
            
            &:focus {
                background: $dark-hard2-background;
                box-shadow: none;
                border-color: $dark-border;
            }
        }
    }
}

// Filters Section
.marketplace-filters {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    
    .category-pills {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }
    
    .category-pill {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 20px;
        border: 2px solid $dark-border2;
        background: $dark-background;
        border-radius: 50px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        color: var(--text-primary);
        
        .pill-icon {
            font-size: 16px;
        }
        
        .pill-label {
            font-size: 14px;
        }
        
        .pill-count {
            background: $dark-hard2-background;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
            color: var(--text-secondary);
        }
        
        &:hover {
            border-color: $protokol-royal-active;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
            background: $dark-hard2-background;
        }
        
        &.active {
            background: $protokol-royal-active;
            border-color: $protokol-royal-active;
            color: var(--text-primary);
            
            .pill-count {
                background: rgba(255, 255, 255, 0.2);
                color: var(--text-primary);
            }
        }
    }
    
    .owned-filter {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 20px;
        border: 2px solid $dark-border2;
        background: $dark-background;
        border-radius: 50px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        color: var(--text-primary);
        font-size: 14px;
        
        &:hover {
            border-color: #10b981;
            transform: translateY(-2px);
            background: $dark-hard2-background;
        }
        
        &.active {
            background: #10b981;
            border-color: #10b981;
            color: var(--text-primary);
        }
    }
}

// Loading State
.loading-state {
    text-align: center;
    padding: 80px 20px;
    
    .loading-text {
        color: var(--text-secondary);
        font-size: 1.1rem;
    }
}

// Empty State
.empty-state {
    text-align: center;
    padding: 80px 20px;
    
    .empty-icon {
        color: $dark-border;
        margin-bottom: 24px;
    }
    
    .empty-title {
        color: rgba(255, 255, 255, 0.9);
        font-size: 1.5rem;
        font-weight: 600;
        margin-bottom: 8px;
    }
    
    .empty-subtitle {
        color: var(--text-muted);
        font-size: 1rem;
    }
}

// Marketplace Grid
.marketplace-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 24px;
    padding-bottom: 40px;
}

.marketplace-card {
    background: $dark-background;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid $dark-border2;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
    position: relative;
    
    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
        border-color: $protokol-royal-active;
        background: $dark-hard2-background;
    }
    
    &.selected {
        border-color: $protokol-royal-active;
        box-shadow: 0 20px 40px rgba(102, 126, 234, 0.3);
        background: $dark-hard2-background;
    }
    
    .discount-badge {
        position: absolute;
        top: 12px;
        right: 12px;
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        color: white;
        padding: 6px 12px;
        border-radius: 8px;
        font-size: 0.85rem;
        font-weight: 700;
        z-index: 10;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        
        span {
            display: block;
            line-height: 1;
        }
    }
    
    .card-image {
        width: 100%;
        height: 180px;
        background: linear-gradient(135deg, $dark-hard2-background 0%, $dark-background 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        border-bottom: 1px solid $dark-border2;
        
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        
        &.card-icon {
            font-size: 64px;
            color: $dark-border;
        }
    }
    
    .card-content {
        padding: 20px;
    }
    
    .card-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 12px;
        min-height: 56px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        line-height: 1.4;
    }
    
    .card-description {
        font-size: 0.85rem;
        color: var(--text-secondary);
        margin-bottom: 12px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        line-height: 1.5;
        min-height: 42px;
    }
    
    .card-price {
        margin-bottom: 12px;
        display: flex;
        align-items: baseline;
        gap: 6px;
        
        .price-amount {
            font-size: 1.5rem;
            font-weight: 700;
            color: $protokol-royal-active;
        }
        
        .price-period {
            font-size: 0.85rem;
            color: var(--text-secondary);
        }
    }
    
    .card-additional-units {
        margin-bottom: 12px;
        font-size: 0.8rem;
        
        .unit-item {
            display: flex;
            align-items: center;
            gap: 6px;
            color: var(--text-secondary);
            margin-bottom: 4px;
            
            .unit-label {
                color: var(--text-secondary);
            }
            
            .unit-price {
                font-weight: 600;
                color: rgba(245, 158, 11, 0.9);
            }
        }
    }
    
    .card-includes {
        margin-bottom: 12px;
        font-size: 0.85rem;
        color: #10b981;
        display: flex;
        align-items: center;
        font-weight: 500;
        
        svg {
            color: #10b981;
        }
    }
    
    .card-badges {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    
    .badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        
        &.badge-type {
            &.badge-integration {
                background: rgba(108, 99, 255, 0.2);
                color: #a5b4f6;
                border: 1px solid rgba(108, 99, 255, 0.3);
            }
            
            &.badge-template {
                background: rgba(251, 191, 36, 0.2);
                color: #fcd34d;
                border: 1px solid rgba(251, 191, 36, 0.3);
            }
            
            &.badge-app {
                background: rgba(16, 185, 129, 0.2);
                color: #6ee7b7;
                border: 1px solid rgba(16, 185, 129, 0.3);
            }
        }
        
        &.badge-owned {
            background: rgba(16, 185, 129, 0.2);
            color: #6ee7b7;
            border: 1px solid rgba(16, 185, 129, 0.3);
        }
        
        &.badge-trial {
            background: rgba(251, 146, 60, 0.2);
            color: #fdba74;
            border: 1px solid rgba(251, 146, 60, 0.3);
        }
        
        &.badge-beta {
            background: rgba(100, 116, 139, 0.2);
            color: #94a3b8;
            border: 1px solid rgba(100, 116, 139, 0.3);
        }
        
        &.badge-active {
            background: rgba(102, 126, 234, 0.2);
            color: #99a9f3;
            border: 1px solid rgba(102, 126, 234, 0.3);
        }
        
        &.badge-free {
            background: rgba(34, 197, 94, 0.2);
            color: #86efac;
            border: 1px solid rgba(34, 197, 94, 0.3);
        }
        
        &.badge-additional-charges {
            background: rgba(245, 158, 11, 0.2);
            color: #fbbf24;
            border: 1px solid rgba(245, 158, 11, 0.3);
        }
        
        &.badge-onboarding {
            background: rgba(139, 92, 246, 0.2);
            color: #c4b5fd;
            border: 1px solid rgba(139, 92, 246, 0.3);
        }
        
        &.badge-expired {
            background: rgba(239, 68, 68, 0.2);
            color: #fca5a5;
            border: 1px solid rgba(239, 68, 68, 0.3);
        }
        
        &.badge-prorated {
            background: rgba(251, 146, 60, 0.15);
            color: #fed7aa;
            border: 1px solid rgba(251, 146, 60, 0.3);
        }
        
        &.badge-multi-product {
            background: rgba(139, 92, 246, 0.15);
            color: #d8b4fe;
            border: 1px solid rgba(139, 92, 246, 0.3);
        }
        
        &.badge-tag {
            background: rgba(100, 116, 139, 0.15);
            color: #cbd5e1;
            border: 1px solid rgba(100, 116, 139, 0.25);
            text-transform: none;
            font-weight: 500;
        }
    }
}

// Responsive Design
@media (max-width: 768px) {
    .marketplace-header {
        .search-wrapper {
            max-width: 100%;
        }
    }
    
    .marketplace-filters {
        .category-pills {
            width: 100%;
        }
        
        .owned-filter {
            width: 100%;
            justify-content: center;
        }
    }
    
    .marketplace-grid {
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 16px;
    }
}

.onboarding-message {
    color: var(--text-secondary);
    font-size: 15px;
    line-height: 1.5;
}

// Trial Button Styling
.trial-button {
    background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%) !important;
    border: none !important;
    color: white !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4) !important;
    transition: all 0.3s ease !important;
    
    &:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 6px 20px rgba(245, 158, 11, 0.6) !important;
        background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%) !important;
    }
    
    &:active {
        transform: translateY(0) !important;
    }
}

// Checkout Button Styling
.mp-divider {
    border: none;
    border-top: 1px solid var(--border-default);
    margin: 1.25rem 0;
}

.iw-section {
    margin-bottom: 1.25rem;
}
.iw-section:last-child { margin-bottom: 0; }

.iw-section-title {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-secondary);
    margin-bottom: 0.65rem;
}

.iw-req-row {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0.55rem 0.75rem;
    border-radius: 8px;
    margin-bottom: 0.35rem;
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
}
.iw-req-row.satisfied { background: rgba(16, 185, 129, 0.07); border-color: rgba(16, 185, 129, 0.2); }
.iw-req-row.unsatisfied { background: rgba(220, 53, 69, 0.07); border-color: rgba(220, 53, 69, 0.2); }
.iw-req-row.optional-unsatisfied { background: var(--bg-card-secondary); }
.iw-req-icon { font-size: 0.95rem; margin-top: 2px; flex-shrink: 0; color: var(--text-secondary); }
.iw-req-name { font-weight: 500; font-size: 0.88rem; color: var(--text-primary); }
.iw-badge { font-size: 0.68rem; }

.iw-perm-row {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    margin-bottom: 0.35rem;
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
}
.iw-perm-icon { font-size: 0.85rem; margin-top: 3px; flex-shrink: 0; }
.iw-perm-name { font-size: 0.82rem; color: var(--text-primary); }
.iw-perm-row--satisfied { background: rgba(16, 185, 129, 0.07); border-color: rgba(16, 185, 129, 0.2); }
.iw-perm-row--unsatisfied { background: rgba(220, 53, 69, 0.07); border-color: rgba(220, 53, 69, 0.2); }

.iw-btn {
    padding: 0.45rem 1.1rem;
    border-radius: 8px;
    border: 1px solid transparent;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: opacity 0.15s, border-color 0.15s;
}
.iw-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.iw-btn-primary { background: var(--primary); color: #fff; border-color: var(--primary); }
.iw-btn-primary:not(:disabled):hover { opacity: 0.85; }
.iw-btn-secondary {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    border-color: var(--border-default);
}
.iw-btn-secondary:not(:disabled):hover { border-color: var(--border-hover); }
.iw-btn-sm { padding: 0.3rem 0.7rem; font-size: 0.8rem; }
.iw-btn-install {
    background: rgba(99, 102, 241, 0.12);
    color: #818cf8;
    border-color: rgba(99, 102, 241, 0.3);
    white-space: nowrap;
    flex-shrink: 0;
    padding: 0.3rem 0.7rem;
    font-size: 0.8rem;
}
.iw-btn-install:not(:disabled):hover {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.5);
}

.iw-expand-btn {
    display: flex;
    align-items: center;
    width: 100%;
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-secondary);
    cursor: pointer;
    gap: 0.35rem;
    transition: border-color 0.15s, background 0.15s;
}
.iw-expand-btn:hover {
    border-color: var(--border-hover);
    background: var(--bg-card);
    color: var(--text-primary);
}
.iw-expand-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: var(--border-default);
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--text-secondary);
}

::v-deep .checkout-button-prominent {
    button {
        background: linear-gradient(135deg, $protokol-royal-active 0%, #5a6fd8 100%) !important;
        border: none !important;
        padding: 12px 32px !important;
        font-size: 16px !important;
        font-weight: 600 !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4) !important;
        transition: all 0.3s ease !important;
        
        &:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6) !important;
            background: linear-gradient(135deg, #7888ea 0%, $protokol-royal-active 100%) !important;
        }
        
        &:active {
            transform: translateY(0) !important;
        }
    }
}
</style>

<script>
import debounce from 'lodash/debounce'
import IntegrationIcon from '../integrations/Icon'
import TemplateDescription from '../templates/Description'
import Template from '../../lib/Template'
import Price from '../../plugins/protokol/lib/Price'
import ConfirmModal from '@/components/workflow/ConfirmModal.vue'
import RedeemCodeModal from '@/components/RedeemCodeModal.vue'


export default {
    name: 'MarketplaceList',
    components: {
        IntegrationIcon,
        TemplateDescription,
        RedeemCodeModal,
        ConfirmModal,
    },
    data() {
        return {
            loading: true,
            searchKeyword: '',
            selectedCategory: 'all',
            showOnlyOwned: false,
            integrations: [],
            templates: [],
            apps: [],
            selectedItem: null,
            selectedItemDetails: null,
            selectedTemplateModel: null,
            showSidebar: false,
            applyingUpdate: false,
            appPreflight: null,
            loadingAppPreflight: false,
            appPreflightError: null,
            installingApp: false,
            uninstallingApp: false,
            appDetail: null,
            loadingAppDetail: false,
            appInstallPermsExpanded: false,
            appRuntimePermsExpanded: false,
            appExposedPermsExpanded: false,
            selectedInstallWorkspaces: [],
            loadingDetails: false,
            installedTemplates: [],
            subscriptions: [],
            trials: [],
            priceHelper: null,
            priceCache: {},
            optionPriceCache: {},
            loadingOptionPrices: false,
            showOnboardingModal: false,
            onboardingMessage: '',
            onboardingIntegrationId: null,
            expandedOption: null,
            selectedOptionCode: null,
            showRedeemCodeModal: false,
            confirmModal: {
                show: false,
                title: '',
                message: '',
                confirmText: 'Confirm',
                danger: false,
                icon: 'warning',
                action: null,
                params: null
            }
        }
    },
    computed: {
        docsUrl() {
            return this.$env.get('DOCS_URL')
        },

        enterpriseMailtoLink() {
            var subject = encodeURIComponent(this.$t('marketplace.enterprise.emailSubject'))
            return 'mailto:office@protokol.io?subject=' + subject
        },

        account() {
            return this.$store.getters.getAccount
        },

        projectWorkspaceOptions() {
            const project = this.$store.getters.getCurrentProject
            return (project && project.workspaces) ? project.workspaces : []
        },

        appPreflightHasRequirements() {
            if (!this.appPreflight) return false
            return (this.appPreflight.integrations && this.appPreflight.integrations.length > 0) ||
                   (this.appPreflight.components && this.appPreflight.components.length > 0)
        },

        
        allItems() {
            const items = []
            
            // Add integrations
            this.integrations.forEach(integration => {
                items.push({
                    ...integration,
                    type: 'integration',
                    id: integration.id
                })
            })
            
            // Add templates
            this.templates.forEach(template => {
                items.push({
                    ...template,
                    type: 'template',
                    id: `template-${template.id}`
                })
            })
            
            // Add apps
            this.apps.forEach(app => {
                items.push({
                    ...app,
                    type: 'app',
                    id: `app-${app.uuid}`,
                    name: app.name
                })
            })
            
            return items
        },
        
        filteredItems() {
            let items = this.allItems
            
            // Filter by category
            if (this.selectedCategory !== 'all') {
                items = items.filter(item => {
                    if (this.selectedCategory === 'integrations') return item.type === 'integration'
                    if (this.selectedCategory === 'templates') return item.type === 'template'
                    if (this.selectedCategory === 'apps') return item.type === 'app'
                    return true
                })
            }
            
            // Filter by owned
            if (this.showOnlyOwned) {
                items = items.filter(item => this.isOwned(item) || this.isActive(item))
            }
            
            // Filter by search keyword
            if (this.searchKeyword.trim()) {
                const keyword = this.searchKeyword.toLowerCase()
                items = items.filter(item => {
                    const label = this.$translateLabel(item.label || item.name || '').toLowerCase()
                    return label.includes(keyword)
                })
            }
            
            return items
        }
    },
    watch: {
        searchKeyword: debounce(function() {
            // Trigger search
        }, 300),
        
        allItems: {
            handler(items) {
                // Fetch prices for all items when they change
                if (items && items.length > 0 && this.priceHelper) {
                    items.forEach(item => {
                        this.fetchPrice(item)
                    })
                }
            }
        }
    },
    methods: {
        onCodeRedeemed() {
            this.loadData()
        },

        filterByCategory(category) {
            this.selectedCategory = category
        },
        
        isProrated(item) {
            if (!item) return false
            const cacheKey = `${item.type}-${item.id}`
            const cached = this.priceCache[cacheKey]
            return cached && typeof cached === 'object' && cached.isProrated
        },
        
        toggleExpandOption(item, option) {
            const optionCode = typeof option === 'string' ? option : option.code
            
            if (this.expandedOption === optionCode) {
                this.expandedOption = null
            } else {
                this.expandedOption = optionCode
            }
            
            // Only allow selecting options that are upgrades (higher level than current)
            if (typeof option === 'object' && this.isOptionDisabled(item, option)) {
                return
            }
            
            this.selectedOptionCode = optionCode
        },
        
        selectItem(item) {
            this.selectedItem = item
            this.showSidebar = true
            this.expandedOption = null
            this.selectedOptionCode = null
            
            // Load details for templates
            if (item.type === 'template') {
                this.loadTemplateDetails(item)
            }

            // Load app detail (runtime_permissions, exposed permissions) for apps
            this.appDetail = null
            this.appInstallPermsExpanded = false
            this.appRuntimePermsExpanded = false
            this.appExposedPermsExpanded = false
            // Pre-select all workspaces for install
            this.selectedInstallWorkspaces = this.projectWorkspaceOptions.map(w => w.uuid)
            if (item.type === 'app' && item.uuid) {
                this.loadAppDetail(item)
            }

            // Load preflight for free uninstalled apps
            this.appPreflight = null
            this.appPreflightError = null
            if (item.type === 'app' && this.isFree(item) && !this.isAppInstalled(item)) {
                this.loadAppPreflight(item)
            }
            
            // Fetch prices for all options if this is an integration with option_set
            if (item.type === 'integration' && item.option_set && item.option_set.items && item.option_set.items.length > 1) {
                this.fetchOptionPrices(item)
                // Preselect the current option (for integrated items) or default
                const currentOption = this.getCurrentOptionForIntegration(item)
                if (currentOption) {
                    this.selectedOptionCode = currentOption.code
                } else {
                    const defaultOption = item.option_set.items.find(opt => opt.default === true)
                    this.selectedOptionCode = defaultOption ? defaultOption.code : item.option_set.items[0].code
                }
            }
        },
        
        loadTemplateDetails(item) {
            this.loadingDetails = true
            this.selectedItemDetails = null
            
            const templateId = item.id.replace('template-', '')
            
            this.$http.get(`/v1/project/template/${templateId}`).then(resp => {
                this.selectedItemDetails = resp.data
                this.selectedTemplateModel = new Template(this.selectedItemDetails, this.$store.getters.getLocale)
            }).catch(() => {
                // Error handled
            }).finally(() => {
                this.loadingDetails = false
            })
        },
        
        isSelected(item) {
            return this.selectedItem && this.selectedItem.id === item.id
        },
        
        isOwned(item) {
            if (item.type === 'integration') {
                // Check both subscription.product_id and subscription.id against item.id and item.product_id
                return this.subscriptions.find(s => 
                    s.product_id == item.id || 
                    s.product_id == item.product_id ||
                    s.id == item.id ||
                    s.id == item.product_id
                ) !== undefined
            }
            if (item.type === 'template') {
                return this.subscriptions.find(s => 
                    s.product_id == item.product_id ||
                    s.id == item.product_id
                ) !== undefined
            }
            if (item.type === 'app') {
                // Check if subscription exists with product_id matching app's uuid
                return this.subscriptions.find(s => 
                    s.product_id == item.uuid
                ) !== undefined
            }
            return false
        },
        
        isTrial(item) {
            if (item.type === 'integration' || item.type === 'template') {
                const sub = this.subscriptions.find(s => s.product_id == item.product_id)
                return sub !== undefined && sub.is_trial
            }
            if (item.type === 'app') {
                const sub = this.subscriptions.find(s => s.product_id == item.uuid)
                return sub !== undefined && sub.is_trial
            }
            return false
        },
        
        isExpired(item) {
            if (item.type === 'integration') {
                const sub = this.subscriptions.find(s => 
                    s.product_id == item.id || 
                    s.product_id == item.product_id ||
                    s.id == item.id ||
                    s.id == item.product_id
                )
                return sub !== undefined && sub.is_expired
            }
            if (item.type === 'template') {
                const sub = this.subscriptions.find(s => 
                    s.product_id == item.product_id ||
                    s.id == item.product_id
                )
                return sub !== undefined && sub.is_expired
            }
            if (item.type === 'app') {
                const sub = this.subscriptions.find(s => s.product_id == item.uuid)
                return sub !== undefined && sub.is_expired
            }
            return false
        },
        
        isActive(item) {
            if (item.type === 'integration') {
                return this.$store.getters.isIntegrationActive(item.id)
            }
            return false
        },
        
        isIntegrated(item) {
            if (item.type === 'integration') {
                const integrations = this.$store.getters.getIntegrations || []
                return integrations.some(i => i.id === item.id)
            }
            return false
        },
        
        canIntegrate(item) {
            if (item.type !== 'integration') return false
            
            // Check if item has option_set with multiple options
            if (item.option_set && item.option_set.items && item.option_set.items.length > 1) {
                // Use selected option, or default option
                const selectedOption = this.getSelectedOption(item)
                if (selectedOption) {
                    const optionPrice = this.getOptionPrice(selectedOption)
                    return optionPrice === 0 && !this.isIntegrated(item)
                }
            }
            
            // Can integrate if owned or free, but not yet integrated
            return (this.isOwned(item) || this.isFree(item)) && !this.isIntegrated(item)
        },
        
        getSelectedOption(item) {
            if (!item || !item.option_set || !item.option_set.items) return null
            
            // Use selectedOptionCode if set
            if (this.selectedOptionCode) {
                return item.option_set.items.find(opt => opt.code === this.selectedOptionCode)
            }
            
            // Otherwise use default option
            const defaultOption = item.option_set.items.find(opt => opt.default === true)
            return defaultOption || item.option_set.items[0]
        },
        
        getCurrentOptionCodeForIntegration(item) {
            if (item.type !== 'integration') return null
            
            const subs = this.subscriptions || []
            const sub = subs.find(s => 
                s.product_id == item.id || 
                s.product_id == item.product_id ||
                s.id == item.id ||
                s.id == item.product_id
            )
            
            if (sub && sub.option_code) {
                return sub.option_code
            }
            
            // option_code is null or undefined, which means default option
            return null
        },
        
        getCurrentOptionForIntegration(item) {
            if (!item.option_set || !item.option_set.items) return null
            
            const currentCode = this.getCurrentOptionCodeForIntegration(item)
            
            // If option_code is null, user is on the default option
            if (!currentCode) {
                const defaultOption = item.option_set.items.find(opt => opt.default === true)
                return defaultOption || item.option_set.items[0]
            }
            
            return item.option_set.items.find(opt => opt.code === currentCode)
        },
        
        isOptionUpgrade(item, targetOption) {
            if (!targetOption || !targetOption.level) return false
            
            const currentOption = this.getCurrentOptionForIntegration(item)
            if (!currentOption || !currentOption.level) return false
            
            return targetOption.level > currentOption.level
        },
        
        isOptionDisabled(item, option) {
            if (!item || !option) return false
            if (!item.option_set || !item.option_set.items) return false
            
            // Only restrict when the integration is already integrated/owned
            if (!this.isIntegrated(item) && !this.isOwned(item)) return false
            
            const currentOption = this.getCurrentOptionForIntegration(item)
            if (!currentOption) return false
            
            // Disable current option and any option with level <= current
            return (option.level || 0) <= (currentOption.level || 0)
        },
        
        shouldShowCheckoutForOption(item) {
            if (item.type !== 'integration') return false
            
            // Check if item has option_set with multiple options
            if (item.option_set && item.option_set.items && item.option_set.items.length > 1) {
                const selectedOption = this.getSelectedOption(item)
                if (!selectedOption) return false
                
                const optionPrice = this.getOptionPrice(selectedOption)
                
                // If integration is active and user selects a different option that's an upgrade, show checkout
                if (this.isIntegrated(item)) {
                    const currentCode = this.getCurrentOptionCodeForIntegration(item)
                    const isDifferentOption = currentCode !== selectedOption.code
                    const isUpgrade = this.isOptionUpgrade(item, selectedOption)
                    
                    console.log('[shouldShowCheckoutForOption]', {
                        itemId: item.id,
                        currentCode,
                        selectedCode: selectedOption.code,
                        isDifferentOption,
                        isUpgrade,
                        optionPrice,
                        willShow: isDifferentOption && isUpgrade && optionPrice > 0
                    })
                    
                    return isDifferentOption && isUpgrade && optionPrice > 0
                }
                
                // If not integrated yet, show checkout for paid options
                // (User might have free subscription but want to purchase paid option before integrating)
                return optionPrice > 0
            }
            
            return false
        },
        
        isFree(item) {
            const price = this.getPrice(item)
            return price <= 0
        },
        
        hasOnboarding(item) {
            if (item.type !== 'integration') return false
            return item.require_onboarding === true || item.require_onboarding === 1
        },
        
        canActivate(item) {
            if (item.type === 'integration') {
                // Can activate if it's integrated but not active
                return this.isIntegrated(item) && !this.isActive(item)
            }
            return false
        },
        
        canInstallTemplate(item) {
            if (item.type !== 'template') return false
            
            const installed = this.isTemplateInstalled(item)
            if (installed) return false
            
            // Don't show install button if expired
            const expired = this.isExpired(item)
            if (expired) return false
            
            const owned = this.isOwned(item)
            const free = this.isFree(item)
            
            return free || owned
        },
        
        canBuyTemplate(item) {
            if (item.type !== 'template') return false
            if (this.isFree(item)) return false
            
            // Always show buy button if subscription is trial
            const trial = this.isTrial(item)
            if (trial) return true
            
            // Otherwise, show buy button only if not owned
            const owned = this.isOwned(item)
            return !owned
        },
        
        canTrialTemplate(item) {
            if (item.type !== 'template') return false
            
            // Don't show trial if template is already installed
            if (this.isTemplateInstalled(item)) return false
            
            // Don't show trial if expired
            if (this.isExpired(item)) return false
            
            // Don't show trial if template can be installed (free or owned)
            if (this.canInstallTemplate(item)) return false
            
            // Don't show trial if trial was already used
            if (this.$store.getters.isTrialUsed(item.product_id)) return false
            
            return this.selectedItemDetails && this.selectedItemDetails.trial_period !== null
        },
        
        isTemplateInstalled(item) {
            if (item.type !== 'template') return false
            const templateId = item.id.replace('template-', '')
            return this.installedTemplates.find(t => t.template.id == templateId) !== undefined
        },
        
        hasIntegrationSettings(item) {
            // An integration with a null/empty settings field has no settings page.
            return !!(item && typeof item.settings === 'string' && item.settings.trim())
        },

        goToIntegrationSettings() {
            const item = this.selectedItem
            if (!this.hasIntegrationSettings(item)) return
            this.$router.push(item.settings)
        },
        
        isAppInstalled(item) {
            if (!item || item.type !== 'app') return false
            const allForgeApps = this.$store.getters.getAllForgeApps || []
            return allForgeApps.some(function(a) { return a.external_id === item.uuid })
        },

        getInstalledApp(item) {
            if (!item || item.type !== 'app') return null
            const allForgeApps = this.$store.getters.getAllForgeApps || []
            const installed = allForgeApps.find(function(a) { return a.external_id === item.uuid }) || null
            if (!installed) return null

            // update_available comes from the workspace list, not the global list — merge it in
            const workspaceApps = this.$store.getters.getForgeApps || []
            const workspaceApp = workspaceApps.find(a => a.uuid === installed.uuid)
            if (workspaceApp && workspaceApp.update_available) {
                return Object.assign({}, installed, { update_available: workspaceApp.update_available })
            }
            return installed
        },

        isUpdateAvailable(item) {
            const installed = this.getInstalledApp(item)
            if (!installed) return false
            return !!installed.update_available
        },

        async applyUpdate() {
            const installed = this.getInstalledApp(this.selectedItem)
            if (!installed) return
            this.applyingUpdate = true
            const env = this.$store.getters.getCurrentEnv || 'dev'
            try {
                const resp = await this.$http.post(
                    '/luma/appservice/v1/forge/' + installed.name + '/apply-update',
                    {},
                    { headers: { 'X-Project-Env': env } }
                )
                const data = resp.data || {}
                if (data.updated) {
                    this.$bvToast.toast(this.$t('marketplace.appUpdateInitiated'), {
                        title: this.$t('marketplace.updating'),
                        variant: 'success',
                        autoHideDelay: 4000
                    })
                    await this.$store.dispatch('refreshForgeApps')
                } else {
                    this.$bvToast.toast(this.$t('marketplace.appAlreadyUpToDate'), {
                        title: this.$t('marketplace.settings'),
                        variant: 'info',
                        autoHideDelay: 3000
                    })
                }
            } catch (err) {
                const message = (err.response && err.response.data && err.response.data.message) || err.message || this.$t('marketplace.failedToUpdate')
                this.$bvToast.toast(message, {
                    title: this.$t('marketplace.error'),
                    variant: 'danger',
                    autoHideDelay: 5000
                })
            } finally {
                this.applyingUpdate = false
            }
        },

        isAppOwnedThroughTemplate(item) {
            if (!item || item.type !== 'app') return false
            // Find subscription for this app
            const subscription = this.subscriptions.find(s => s.product_id == item.uuid)
            // If subscription exists but has no license_code, it's owned through a template
            return subscription !== undefined && !subscription.license_code
        },

        getIntegrationLabel(id) {
            var integration = (this.integrations || []).find(function(i) { return i.id === id })
            if (integration) {
                return this.$translateLabel(integration.label || integration.name || id)
            }
            return id
        },

        installIntegrationFromPreflight(req) {
            var url = '/marketplace/install?id=' + encodeURIComponent(req.id) + '&type=integration'
            window.open(url, '_blank')
        },

        loadAppPreflight(item) {
            var app = item || this.selectedItem
            if (!app) return
            this.loadingAppPreflight = true
            this.appPreflightError = null
            this.appPreflight = null
            this.$http.get('/luma/appservice/v1/forge/' + app.uuid + '/preflight')
                .then(function(resp) {
                    this.appPreflight = resp.data
                }.bind(this))
                .catch(function(err) {
                    this.appPreflightError = (err.response && err.response.data && err.response.data.message) || this.$t('marketplace.installWizard.preflightFailed')
                }.bind(this))
                .finally(function() {
                    this.loadingAppPreflight = false
                }.bind(this))
        },

        loadAppDetail(item) {
            var app = item || this.selectedItem
            if (!app || !app.uuid) return
            this.loadingAppDetail = true
            this.$http.get('/luma/appservice/v1/forge/marketplace/' + app.uuid)
                .then(function(resp) {
                    this.appDetail = resp.data
                }.bind(this))
                .catch(function() {
                    // non-critical, sections simply won't show
                }.bind(this))
                .finally(function() {
                    this.loadingAppDetail = false
                }.bind(this))
        },

        doInstallApp() {
            if (!this.selectedItem || this.installingApp) return
            if (this.appPreflight && !this.appPreflight.ready) return
            this.installingApp = true
            this.$http.post('/luma/appservice/v1/forge/install', {
                external_id: this.selectedItem.uuid,
                workspaces: this.selectedInstallWorkspaces,
            })
                .then(function() {
                    this.$store.dispatch('fetchAllForgeApps')
                    this.$store.dispatch('fetchComponents')
                    this.$store.dispatch('fetchIntegrations', true)
                    this.$store.dispatch('getWorkflows')
                    this.$store.commit('RELOAD_CONTAINER')
                    this.$bvToast.toast(this.$t('marketplace.appInstalled'), {
                        variant: 'success',
                        title: this.$t('success')
                    })
                    this.showSidebar = false
                }.bind(this))
                .catch(function(err) {
                    this.$bvToast.toast(
                        (err.response && err.response.data && err.response.data.message) || this.$t('marketplace.failedToInstallApp'),
                        { variant: 'danger', title: this.$t('error') }
                    )
                }.bind(this))
                .finally(function() {
                    this.installingApp = false
                }.bind(this))
        },

        goToAppSettings() {
            const { uuid } = this.selectedItem
            this.$store.dispatch("fetchAllForgeApps").then(() => {
                const app = this.$store.getters.getAllForgeApps.find(a => a.external_id === uuid)
                if (app) {
                    this.$router.push(`/admin/forge/${app.uuid}`)
                } else {
                    this.$mainDialog.msgBoxOk(this.$t('marketplace.appNotFound'), {
                        title: this.$t('marketplace.error'),
                        okVariant: 'danger'
                    })
                }
            })
        },
        
        async integrateIntegration() {
            const popup = this.$loadingPopup(this.$t('marketplace.integratingIntegration'))

            const integrationId = this.selectedItem.id

            try {
                await this.$integrationApi.post('/integrate', {
                    id: integrationId,
                    data: {},
                    integrator: 'platform'
                })

                await this.$store.dispatch('fetchIntegrations', true)
                this.$store.dispatch('fetchComponents')
                this.$store.dispatch('fetchAllForgeApps')
                this.$store.dispatch('getWorkflows')

                this.$bvToast.toast(this.$t('marketplace.integrationIntegrated'), {
                    title: this.$t('marketplace.success'),
                    variant: 'success',
                    toaster: 'b-toaster-top-right',
                    solid: true,
                    autoHideDelay: 3000,
                })

                popup.close()

                const integration = this.$store.getters.getIntegrations.find(int => int.id === integrationId)
                if (integration && integration.onboarding && integration.onboarding.status !== 'completed') {
                    this.showSidebar = false
                    this.$store.commit("UNPAUSE_ONBOARDING_ALL")
                    this.$store.commit("REFRESH_ONBOARDING")
                }
            } catch(e) {
                this.$unknownErrorModal.open(e)
            } finally {
                popup.hide()
            }
        },
        
        activateIntegration() {
            const popup = this.$loadingPopup(this.$t('marketplace.activatingIntegration'))
            
            const integrationId = this.selectedItem.id
            
            this.$integrationApi.post(`/integration/${integrationId}/activate`).then(() => {
                // Update integration status in store
                this.$store.dispatch('fetchIntegrations', true)
                
                this.$bvToast.toast(this.$t('marketplace.integrationActivated'), {
                    variant: 'success',
                    title: this.$t('marketplace.success'),
                    autoHideDelay: 3000
                })
            }).catch(err => {
                console.error('Integration activation error:', err)
                
                const errorData = err.response && err.response.data
                
                // Check if integration requires onboarding
                if (errorData && errorData.require_onboarding && errorData.integration) {
                    this.onboardingMessage = this.$t('marketplace.integrationRequiresOnboarding')
                    this.onboardingIntegrationId = integrationId
                    this.showOnboardingModal = true
                } else {
                    this.$mainDialog.msgBoxOk(
                        (errorData && errorData.message) || this.$t('marketplace.failedToActivate'),
                        {
                            title: this.$t('marketplace.error'),
                            okVariant: 'danger'
                        }
                    )
                }
            }).finally(() => {
                popup.hide()
            })
        },

        startOnboarding() {
            this.showOnboardingModal = false
            // Unpause integration onboarding to trigger the onboarding flow
            this.$store.commit("UNPAUSE_ONBOARDING_ALL")
        },

        cancelOnboarding() {
            this.showOnboardingModal = false
            this.onboardingMessage = ''
            this.onboardingIntegrationId = null
        },
        
        deactivateIntegration() {
            const popup = this.$loadingPopup(this.$t('marketplace.deactivatingIntegration'))
            
            const integrationId = this.selectedItem.id
            
            this.$integrationApi.post(`/integration/${integrationId}/deactivate`).then(() => {
                // Update integration status in store
                this.$store.dispatch('fetchIntegrations', true)
                
                this.$bvToast.toast(this.$t('marketplace.integrationDeactivated'), {
                    variant: 'success',
                    title: this.$t('marketplace.success'),
                    autoHideDelay: 3000
                })
            }).catch(err => {
                this.$mainDialog.msgBoxOk(
                    (err.response && err.response.data && err.response.data.message) || this.$t('marketplace.failedToDeactivate'),
                    {
                        title: this.$t('marketplace.error'),
                        okVariant: 'danger'
                    }
                )
            }).finally(() => {
                popup.hide()
            })
        },
        
        async installTemplate() {
            const templateId = this.selectedItem.id.replace('template-', '')

            this.$bvToast.toast(this.$t('marketplace.installingTemplate'), {
                id: 'template-install',
                variant: 'info',
                title: this.$t('marketplace.installing'),
                autoHideDelay: 30000,
                appendToast: false
            })

            try {
                const resp = await this.$http.post("/v1/project/template/install", {
                    id: templateId,
                    data: {
                        components: this.selectedItemDetails.components,
                        integrations: this.selectedItemDetails.integrations,
                        apps: this.selectedItemDetails.apps,
                        functions: this.selectedItemDetails.functions
                    }
                })

                if (resp) {
                    this.$store.commit('RELOAD_CONTAINER')
                    this.$store.dispatch('fetchComponents')
                    await this.$store.dispatch('fetchIntegrations', true)
                    this.$store.dispatch('fetchAllForgeApps')
                    this.$store.dispatch('getWorkflows')
                }
                this.loadInstalledTemplates()

                const onboardingRequired = this.$store.getters.getOnboardingRequiredIntegrations || []
                if (onboardingRequired.length > 0) {
                    this.showSidebar = false
                    this.$store.commit("UNPAUSE_ONBOARDING_ALL")
                    this.$store.commit("REFRESH_ONBOARDING")
                }
            } catch(e) {
                this.$mainDialog.msgBoxOk(this.$t('marketplace.failedToInstall'))
            } finally {
                this.$bvToast.hide('template-install')
            }
        },
        
        buyTemplate() {
            const templateId = this.selectedItem.id.replace('template-', '')
            
            const popup = this.$loadingPopup()
            
            this.$transactionApi.post("/checkout/link", {
                product_type: "template",
                reference: templateId,
                lang: this.$store.getters.getLocale,
                source: `marketplace:template:${templateId}`
            }).then(resp => {
                window.open(resp.data, '_blank')
            }).catch(() => {
                this.$mainDialog.msgBoxOk(this.$t('marketplace.failedToCheckout'))
            }).finally(() => {
                popup.hide()
            })
        },
        
        startTemplateTrial() {
            const templateId = this.selectedItem.id.replace('template-', '')
            
            const popup = this.$loadingPopup()
            
            this.$transactions.startTrial({
                product_type: "template",
                reference: templateId,
                source: "template"
            }).then((resp) => {
                this.$bvToast.toast(this.$t('marketplace.trialActive'), {
                    variant: 'success',
                    title: this.$t('success')
                })
                
                resp.data.forEach(d => {
                    this.$store.commit("ADD_TRIAL", d)
                })
                
                this.$store.dispatch("fetchSubscriptions", true)
                this.loadData()
            }).catch(() => {
                this.$bvToast.toast(this.$t('marketplace.failedToStartTrial'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            }).finally(() => {
                popup.hide()
            })
        },
        
        uninstallTemplate() {
            const templateId = this.selectedItem.id.replace('template-', '')
            
            this.confirmModal = {
                show: true,
                title: this.$t('marketplace.uninstall'),
                message: this.$t('marketplace.confirmUninstall'),
                confirmText: this.$t('marketplace.uninstall'),
                danger: true,
                icon: 'danger',
                action: 'uninstallTemplate',
                params: templateId
            }
        },
        
        handleConfirmAction() {
            if (this.confirmModal.action === 'uninstallTemplate') {
                this.executeUninstallTemplate(this.confirmModal.params)
            } else if (this.confirmModal.action === 'uninstallApp') {
                this.executeUninstallApp(this.confirmModal.params)
            }
            this.closeConfirmModal()
        },

        doUninstallApp() {
            if (!this.selectedItem) return
            this.confirmModal = {
                show: true,
                title: this.$t('marketplace.uninstall'),
                message: this.$t('marketplace.confirmUninstall'),
                confirmText: this.$t('marketplace.uninstall'),
                danger: true,
                icon: 'danger',
                action: 'uninstallApp',
                params: this.selectedItem.uuid,
            }
        },

        executeUninstallApp(externalId) {
            this.uninstallingApp = true
            const popup = this.$loadingPopup(this.$t('marketplace.uninstalling'))
            this.$http.post('/luma/appservice/v1/forge/uninstall', { external_id: externalId })
                .then(() => {
                    this.$store.dispatch('fetchAllForgeApps')
                    this.$store.dispatch('fetchComponents')
                    this.$store.dispatch('fetchIntegrations', true)
                    this.$store.dispatch('getWorkflows')
                    this.$store.commit('RELOAD_CONTAINER')
                    this.$bvToast.toast(this.$t('marketplace.appUninstalled'), {
                        variant: 'success',
                        title: this.$t('success')
                    })
                    this.showSidebar = false
                })
                .catch(() => {
                    this.$bvToast.toast(this.$t('marketplace.failedToUninstall'), {
                        variant: 'danger',
                        title: this.$t('error')
                    })
                })
                .finally(() => {
                    this.uninstallingApp = false
                    popup.hide()
                })
        },
        
        closeConfirmModal() {
            this.confirmModal = {
                show: false,
                title: '',
                message: '',
                confirmText: 'Confirm',
                danger: false,
                icon: 'warning',
                action: null,
                params: null
            }
        },
        
        executeUninstallTemplate(templateId) {
            const popup = this.$loadingPopup(this.$t('marketplace.uninstalling'))
            
            this.$http.post("/v1/project/template/uninstall", { id: templateId }).then(() => {
                this.$store.commit('RELOAD_CONTAINER')
                this.loadInstalledTemplates()
            }).catch(() => {
                this.$bvToast.toast(this.$t('marketplace.failedToUninstall'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            }).finally(() => {
                popup.hide()
            })
        },
        
        async loadData() {
            this.loading = true
            
            try {
                // Load integrations
                const integrationsData = await this.$store.dispatch("fetchIntegrationsExplore")
                this.integrations = integrationsData || []
                
                // Load templates - both explore and available
                const [exploreResp, availableResp] = await Promise.all([
                    this.$http.post("/v1/project/template/search", { keyword: "", type: 'name' }),
                    this.$http.get("/v1/project/template/available")
                ])
                
                const exploreTemplates = exploreResp.data || []
                const availableTemplates = availableResp.data || []
                
                // Combine and deduplicate templates
                const templateMap = new Map()
                
                availableTemplates.forEach(t => {
                    templateMap.set(t.id, t)
                })
                
                exploreTemplates.forEach(t => {
                    if (!templateMap.has(t.id)) {
                        templateMap.set(t.id, t)
                    }
                })
                
                this.templates = Array.from(templateMap.values())
                
                // Load apps from forge marketplace
                try {
                    const appsResp = await this.$http.get('/luma/appservice/v1/forge/marketplace', {
                        params: { search: this.searchKeyword || '' }
                    })
                    this.apps = appsResp.data || []
                } catch (err) {
                    console.error('Failed to load apps:', err)
                    this.apps = []
                }
                
                // Load installed templates
                await this.loadInstalledTemplates()
                
            } catch (err) {
                console.error('Failed to load marketplace:', err)
                this.$bvToast.toast(this.$t('marketplace.failedToLoad'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            } finally {
                this.loading = false
            }
        },
        
        async loadInstalledTemplates() {
            try {
                const { data = [] } = await this.$http.get("/v1/project/template/workspace")
                this.installedTemplates = data || []
            } catch (err) {
                console.error('Failed to load installed templates:', err)
            }
        },
        
        getIntegrationDescription(item) {
            if (!item || item.type !== 'integration') return null
            
            // Check for localized description object
            if (item.description && typeof item.description === 'object' && item.description.locales) {
                const currentLocale = this.$store.getters.getLocale || 'en_US'
                // Try current locale first, fallback to en_US
                return item.description.locales[currentLocale] || item.description.locales['en_US'] || null
            }
            
            // Check if there's a direct string description property
            if (item.description && typeof item.description === 'string') {
                return item.description
            }
            
            // Try to get description from translation key (e.g., integrations.clerk.description)
            const integrationId = item.id
            const translationKey = `integrations.${integrationId}.description`
            const translated = this.$t(translationKey)
            
            // If translation exists and is not the same as the key, return it
            if (translated && translated !== translationKey) {
                return translated
            }
            
            return null
        },
        
        getPrice(item) {
            if (!item) return null

            // Return cached price first (includes discounts/proration from pricing API)
            const cacheKey = `${item.type}-${item.id}`
            const cached = this.priceCache[cacheKey]

            if (cached !== undefined) {
                // If owned, always show total price (regular price)
                // Otherwise, show prorated if available
                if (cached && typeof cached === 'object') {
                    if (this.isOwned(item)) {
                        return cached.total
                    }
                    return cached.isProrated ? cached.prorated : cached.total
                }
                return cached
            }
            
            // Check if item has option_set with default pricing
            // Skip for apps: their price comes from the transactions API (includes hard-dep integrations)
            if (item.type !== 'app' && item.option_set && item.option_set.items && item.option_set.items.length > 0) {
                const defaultOption = item.option_set.items.find(opt => opt.default === true);
                const selectedOption = defaultOption || item.option_set.items[0];
                
                if (selectedOption && selectedOption.price) {
                    const currency = this.priceHelper ? this.priceHelper.getCurrency() : 'USD';
                    const raw = currency === 'USD' ? selectedOption.price.usd : selectedOption.price.rsd;
                    return Number((raw / 100).toFixed(2));
                }
            }
            
            // Return null while fetching (will be updated)
            return null
        },
        
        hasMultipleOptions(item) {
            return item && 
                   item.option_set && 
                   item.option_set.items && 
                   item.option_set.items.length > 1;
        },
        
        isCurrentOption(item, option) {
            if (!item || !option) return false;
            
            // Check if user has a subscription for this integration
            const subs = this.subscriptions || [];
            const integration = subs.find(s => 
                s.product_id == item.id || 
                s.product_id == item.product_id ||
                s.id == item.id ||
                s.id == item.product_id
            );
            
            if (integration && integration.option_code) {
                return integration.option_code === option.code;
            }
            
            // If no subscription, default option is current
            return option.default === true;
        },
        
        formatPolicyValue(policy) {
            if (!policy) return '';
            
            // Check if this is a size/bytes value
            if (policy.name && policy.name.includes('size')) {
                return this.formatBytes(policy.value);
            }
            return policy.value;
        },
        
        getPolicyLabel(policy) {
            if (!policy) return '';
            
            // Check if policy has a label with locales
            if (policy.label && policy.label.locales) {
                return this.$translateLabel(policy.label);
            }
            
            // Fallback: format the policy name nicely
            if (policy.name) {
                return policy.name
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }
            
            return '';
        },
        
        formatBytes(bytes) {
            if (!bytes || bytes === 0) return '0 Bytes';
            
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        },

        getOptionPrice(option) {
            if (!option) return 0;
            
            // Check if we have fetched price from API
            if (this.selectedItem && this.selectedItem.id) {
                const cacheKey = `${this.selectedItem.type}-${this.selectedItem.id}-${option.code}`
                const cachedPrice = this.optionPriceCache[cacheKey]
                
                if (cachedPrice && cachedPrice.total !== undefined) {
                    return cachedPrice.total
                }
            }
            
            // Fallback to static price from option_set
            if (!option.price) return 0;
            const currency = this.priceHelper ? this.priceHelper.getCurrency() : 'USD';
            const raw = currency === 'RSD' ? option.price.rsd : option.price.usd;
            return Number((raw / 100).toFixed(2));
        },

        formatOptionPrice(option) {
            const price = this.getOptionPrice(option);
            const currency = this.priceHelper ? this.priceHelper.getCurrency() : 'USD';
            
            // Check if we have prorated price for this option
            if (this.selectedItem && this.selectedItem.id) {
                const cacheKey = `${this.selectedItem.type}-${this.selectedItem.id}-${option.code}`
                const cachedPrice = this.optionPriceCache[cacheKey]
                
                if (cachedPrice && cachedPrice.isProrated) {
                    const proratedPrice = cachedPrice.prorated
                    const totalPrice = cachedPrice.total
                    
                    if (currency === 'RSD') {
                        return `${proratedPrice} RSD<br><span style="text-decoration: line-through; opacity: 0.5; font-size: 0.85em;">${totalPrice} RSD</span>`
                    }
                    return `$${proratedPrice}<br><span style="text-decoration: line-through; opacity: 0.5; font-size: 0.85em;">$${totalPrice}</span>`
                }
            }
            
            if (currency === 'RSD') {
                return `${price} RSD`;
            }
            return `$${price}`;
        },
        
        getOptionUsagePrices(option) {
            if (!option || !this.selectedItem || !this.selectedItem.id) return []
            
            const cacheKey = `${this.selectedItem.type}-${this.selectedItem.id}-${option.code}`
            const cachedPrice = this.optionPriceCache[cacheKey]
            
            if (cachedPrice && cachedPrice.usagePrices) {
                return cachedPrice.usagePrices
            }
            
            return []
        },
        
        getOptionDiscounts(option) {
            if (!option || !this.selectedItem || !this.selectedItem.id) return []
            
            const cacheKey = `${this.selectedItem.type}-${this.selectedItem.id}-${option.code}`
            const cachedPrice = this.optionPriceCache[cacheKey]
            
            if (cachedPrice && cachedPrice.productDiscounts) {
                return cachedPrice.productDiscounts
            }
            
            return []
        },
        
        isOptionProrated(option) {
            if (!option || !this.selectedItem || !this.selectedItem.id) return false
            
            const cacheKey = `${this.selectedItem.type}-${this.selectedItem.id}-${option.code}`
            const cachedPrice = this.optionPriceCache[cacheKey]
            
            return cachedPrice && cachedPrice.isProrated
        },
        
        hasAnyOptionProrated(item) {
            if (!item || !item.option_set || !item.option_set.items) return false
            
            return item.option_set.items.some(option => {
                const cacheKey = `${item.type}-${item.id}-${option.code}`
                const cachedPrice = this.optionPriceCache[cacheKey]
                return cachedPrice && cachedPrice.isProrated
            })
        },
        
        getCheckoutOptions(item) {
            if (!item) return {}
            
            // If item has option_set, include selected variant
            if (item.option_set && item.option_set.items && item.option_set.items.length > 1) {
                const selectedOption = this.getSelectedOption(item)
                if (selectedOption) {
                    return { variant: selectedOption.code }
                }
            }
            
            return {}
        },
        
        formatPrice(item) {
            if (!item) return null
            
            const cacheKey = `${item.type}-${item.id}`
            const cached = this.priceCache[cacheKey]
            
            if (cached === undefined || cached === null) return null
            
            const currency = this.priceHelper.getCurrency()
            const isOwned = this.isOwned(item)
            const isTrial = this.isTrial(item)
            
            // Handle object format (new format with prorated info)
            if (typeof cached === 'object') {
                // If owned (but not trial), show total price. If not owned or is trial, show prorated if available
                const price = (isOwned && !isTrial) ? cached.total : (cached.isProrated ? cached.prorated : cached.total)
                
                // Format price with currency symbol
                let formattedPrice = ''
                if (currency === 'USD') {
                    formattedPrice = `$${price}`
                } else if (currency === 'RSD') {
                    formattedPrice = `${price} RSD`
                } else {
                    formattedPrice = `${price} ${currency}`
                }
                
                // Add strikethrough total and prorated badge if prorated AND (not owned OR is trial)
                if (cached.isProrated && (!isOwned || isTrial)) {
                    const totalFormatted = currency === 'USD' ? `$${cached.total}` : 
                                         currency === 'RSD' ? `${cached.total} RSD` : 
                                         `${cached.total} ${currency}`
                    return `${formattedPrice}<br><span style="text-decoration: line-through; opacity: 0.5; font-size: 0.65em; line-height: 0.5; display: inline-block; margin-top: -4px;">${totalFormatted}</span>`
                }
                
                return formattedPrice
            }
            
            // Handle legacy number format
            const price = cached
            
            // Format price with currency symbol
            if (currency === 'USD') {
                return `$${price}`
            } else if (currency === 'RSD') {
                return `${price} RSD`
            }
            
            return `${price} ${currency}`
        },
        
        getUsagePrices(item) {
            if (!item) return []
            
            const cacheKey = `${item.type}-${item.id}`
            const cached = this.priceCache[cacheKey]
            
            if (cached && cached.usagePrices) {
                return cached.usagePrices
            }
            
            return []
        },

        getProductDiscounts(item) {
            if (!item) return []
            
            const cacheKey = `${item.type}-${item.id}`
            const cached = this.priceCache[cacheKey]
            
            if (cached && cached.productDiscounts) {
                return cached.productDiscounts
            }
            
            return []
        },

        getPriceProducts(item) {
            if (!item) return []
            
            const cacheKey = `${item.type}-${item.id}`
            const cached = this.priceCache[cacheKey]
            
            if (cached && Array.isArray(cached.products)) {
                return cached.products
            }
            
            return []
        },

        hasMultipleProducts(item) {
            return this.getPriceProducts(item).length > 1
        },

        formatProductPrice(product) {
            if (!product || !product.price) return ''
            const currency = this.priceHelper ? this.priceHelper.getCurrency() : 'USD'
            const raw = product.price[currency.toLowerCase()] || product.price[currency] || product.price['usd'] || 0
            const amount = (raw / 100).toFixed(2)
            if (currency === 'USD') return `$${amount}`
            if (currency === 'RSD') return `${amount} RSD`
            return `${amount} ${currency}`
        },

        hasAnyDiscount(item) {
            const discounts = this.getProductDiscounts(item)
            return discounts && discounts.length > 0
        },

        getDiscountBadgeText(item) {
            const discounts = this.getProductDiscounts(item)
            if (!discounts || discounts.length === 0) return ''
            
            // If there's only one discount, show it directly
            if (discounts.length === 1) {
                return `-${this.formatDiscount(discounts[0].discount)}`
            }
            
            // If multiple discounts, check if they're all the same type
            const firstDiscount = discounts[0].discount
            const allSameType = discounts.every(pd => pd.discount.type === firstDiscount.type)
            
            if (allSameType && firstDiscount.type === 'percent') {
                // Show the highest percentage
                const maxPercent = Math.max(...discounts.map(pd => pd.discount.amount))
                return `-${maxPercent}%`
            } else if (allSameType && firstDiscount.type === 'fixed') {
                // Sum all fixed discounts
                const totalAmount = discounts.reduce((sum, pd) => sum + parseFloat(pd.discount.amount), 0)
                const currency = this.priceHelper.getCurrency()
                if (currency === 'USD') {
                    return `-$${totalAmount}`
                } else if (currency === 'RSD') {
                    return `-${totalAmount} RSD`
                }
                return `-${totalAmount} ${currency}`
            } else {
                // Mixed types, just show generic discount indicator
                return this.$t('marketplace.discount')
            }
        },

        formatDiscount(discount) {
            if (!discount) return ''
            
            if (discount.type === 'percent') {
                return `${discount.amount}%`
            } else if (discount.type === 'fixed') {
                const currency = this.priceHelper.getCurrency()
                if (currency === 'USD') {
                    return `$${discount.amount}`
                } else if (currency === 'RSD') {
                    return `${discount.amount} RSD`
                }
                return `${discount.amount} ${currency}`
            }
            
            return ''
        },
        
        formatDiscountPrice(price) {
            if (!price) return ''
            
            const currency = this.priceHelper.getCurrency()
            const formattedPrice = parseFloat(price).toFixed(2)
            
            if (currency === 'USD') {
                return `$${formattedPrice}`
            } else if (currency === 'RSD') {
                return `${formattedPrice} RSD`
            }
            return `${formattedPrice} ${currency}`
        },
        
        formatUsagePrice(priceObj) {
            if (!priceObj) return ''
            
            const currency = this.priceHelper.getCurrency()
            const priceInCents = priceObj[currency] || priceObj[currency.toLowerCase()] || priceObj.USD || priceObj.usd || 0
            // Convert from cents to main currency unit
            const price = (priceInCents / 100).toFixed(2)
            
            if (currency === 'USD') {
                return `$${price}`
            } else if (currency === 'RSD') {
                return `${price} RSD`
            }
            
            return `${price} ${currency}`
        },
        
        formatUnitPrice(pricePerUnit) {
            if (!pricePerUnit) return ''
            
            const currency = this.priceHelper.getCurrency()
            const priceInCents = pricePerUnit[currency.toLowerCase()] || pricePerUnit.usd || 0
            // Convert from cents to dollars/main currency unit
            const price = (priceInCents / 100).toFixed(2)
            
            if (currency === 'USD') {
                return `$${price}`
            } else if (currency === 'RSD') {
                return `${price} RSD`
            }
            
            return `${price} ${currency}`
        },
        
        getAppDescription(item) {
            if (!item || item.type !== 'app') return null
            
            // Check for localized description object
            if (item.description && typeof item.description === 'object' && item.description.locales) {
                const currentLocale = this.$store.getters.getLocale || 'en_US'
                // Map locale format (e.g., en_US -> en)
                const shortLocale = currentLocale.split('_')[0]
                // Try current locale first, fallback to en
                return item.description.locales[shortLocale] || item.description.locales['en'] || null
            }
            
            // Check if there's a direct string description property
            if (item.description && typeof item.description === 'string') {
                return item.description
            }
            
            return null
        },
        
        getAppPrice(item) {
            if (!item || item.type !== 'app') return null
            if (!item.price || typeof item.price !== 'object') return null
            
            const currency = this.priceHelper.getCurrency()
            // Try to get price for current currency (both uppercase and lowercase), fallback to USD
            const price = item.price[currency] || item.price[currency.toUpperCase()] || 
                         item.price[currency.toLowerCase()] || item.price['USD'] || 
                         item.price['usd'] || 0
            
            // Convert from cents to main currency unit
            return (price / 100).toFixed(2)
        },
        
        formatAppPrice(item) {
            const price = this.getAppPrice(item)
            if (!price || price === '0.00') return null
            
            const currency = this.priceHelper.getCurrency()
            
            if (currency === 'USD') {
                return `$${price}`
            } else if (currency === 'EUR') {
                return `€${price}`
            } else if (currency === 'GBP') {
                return `£${price}`
            } else if (currency === 'RSD') {
                return `${price} RSD`
            }
            
            return `${price} ${currency}`
        },
        
        async fetchOptionPrices(item) {
            if (!item || !item.option_set || !item.option_set.items) return
            
            this.loadingOptionPrices = true
            const currency = this.priceHelper ? this.priceHelper.getCurrency() : 'USD'
            
            // Fetch price for each option
            const pricePromises = item.option_set.items.map(async (option) => {
                const cacheKey = `${item.type}-${item.id}-${option.code}`
                
                // Skip if already cached
                if (this.optionPriceCache[cacheKey] !== undefined) return
                
                try {
                    const resp = await this.$transactions.getPricesForProduct('integration', item.id, currency, option.code)
                    
                    const totalAmount = resp.data.total_amount.toFixed(2)
                    const proratedAmount = resp.data.prorated_amount ? resp.data.prorated_amount.toFixed(2) : totalAmount
                    const isProrated = resp.data.is_prorated || false
                    const usagePrices = Array.isArray(resp.data.usage_price) ? resp.data.usage_price : []
                    const productDiscounts = resp.data.product_discounts || []
                    
                    this.$set(this.optionPriceCache, cacheKey, {
                        total: parseFloat(totalAmount),
                        prorated: parseFloat(proratedAmount),
                        isProrated,
                        usagePrices,
                        productDiscounts
                    })
                } catch (err) {
                    console.error(`Failed to fetch price for option ${option.code}:`, err)
                    // Cache error state to prevent retry loops
                    this.$set(this.optionPriceCache, cacheKey, null)
                }
            })
            
            await Promise.all(pricePromises)
            this.loadingOptionPrices = false
        },
        
        async fetchPrice(item) {
            if (!item) return
            
            const cacheKey = `${item.type}-${item.id}`
            
            // Skip if already cached
            if (this.priceCache[cacheKey] !== undefined) return
            
            // Skip if a fetch is already in-flight for this key (prevents double requests
            // when the allItems watcher fires multiple times during sequential data loading)
            if (!this._priceFetchingKeys) this._priceFetchingKeys = new Set()
            if (this._priceFetchingKeys.has(cacheKey)) return
            this._priceFetchingKeys.add(cacheKey)
            
            try {
                // Check if item is free before making API call.
                // But if it has additional_charges, we still need the API call to get usage prices.
                if (item.free === true && !item.additional_charges) {
                    this.$set(this.priceCache, cacheKey, { total: 0, prorated: 0, isProrated: false, usagePrices: [] })
                    return
                }
                
                // Check if price object exists and all values are 0
                // Skip for apps: their static price may be 0 in the DB but the transactions
                // API is the source of truth (includes hard-dep integration costs)
                if (item.type !== 'app' && !item.additional_charges && item.price && typeof item.price === 'object') {
                    const allZero = Object.values(item.price).every(val => val === 0 || val === null || val === undefined)
                    if (allZero) {
                        this.$set(this.priceCache, cacheKey, { total: 0, prorated: 0, isProrated: false, usagePrices: [] })
                        return
                    }
                }
                
                let productType, reference
                let variant = null
                
                if (item.type === 'template') {
                    productType = 'template'
                    reference = item.id.replace('template-', '')
                } else if (item.type === 'app') {
                    productType = 'app'
                    reference = item.uuid
                } else {
                    productType = 'integration'
                    reference = item.id
                    // For option-set integrations fetch default option price from API.
                    if (item.option_set && item.option_set.items && item.option_set.items.length > 0) {
                        const defaultOption = item.option_set.items.find(opt => opt.default === true)
                        const selectedOption = defaultOption || item.option_set.items[0]
                        if (selectedOption && selectedOption.code) {
                            variant = selectedOption.code
                        }
                    }
                }
                
                const currency = this.priceHelper.getCurrency()
                
                const resp = await this.$transactions.getPricesForProduct(productType, reference, currency, variant)
                
                // Cache both prices, whether it's prorated, usage prices, and product discounts
                const totalAmount = resp.data.total_amount.toFixed(2)
                const proratedAmount = resp.data.prorated_amount.toFixed(2)
                this.$set(this.priceCache, cacheKey, {
                    total: totalAmount,
                    prorated: proratedAmount,
                    isProrated: totalAmount !== proratedAmount,
                    usagePrices: Array.isArray(resp.data.usage_price) ? resp.data.usage_price : [],
                    productDiscounts: Array.isArray(resp.data.product_discounts) ? resp.data.product_discounts : [],
                    products: Array.isArray(resp.data.products) ? resp.data.products : []
                })
            } catch (err) {
                console.error('Failed to fetch price for', item.id, err)
                this.$set(this.priceCache, cacheKey, null)
            } finally {
                this._priceFetchingKeys.delete(cacheKey)
            }
        }
    },
    
    created() {
        // Don't initialize priceHelper here - wait for mounted when user data is guaranteed to be loaded
    },
    
    async mounted() {
        // Wait for user to be available in store
        let user = this.$store.getters.getUser
        
        // If user is not loaded yet, wait a bit for it to load
        if (!user || !user.settings || !user.settings.billing_address) {
            // Wait for store to be populated
            await new Promise(resolve => {
                const checkUser = setInterval(() => {
                    user = this.$store.getters.getUser
                    if (user && user.settings && user.settings.billing_address) {
                        clearInterval(checkUser)
                        resolve()
                    }
                }, 50)
                
                // Timeout after 3 seconds
                setTimeout(() => {
                    clearInterval(checkUser)
                    resolve()
                }, 3000)
            })
        }
        
        // Now initialize priceHelper with correct user data
        user = this.$store.getters.getUser
        const billingAddress = user && user.settings && user.settings.billing_address
        const country = billingAddress ? billingAddress.country : 'en_US'
        
        this.priceHelper = new Price(country, this.$store.getters.getLocale)
        
        // Load subscriptions and trials
        this.subscriptions = this.$store.getters.getSubscriptions || []
        this.trials = this.$store.getters.getTrials || []
        
        await Promise.all([
            this.$store.dispatch('fetchSubscriptions'),
            this.$store.dispatch('fetchTrials'),
            this.$store.dispatch('fetchIntegrations')
        ])
        
        this.subscriptions = this.$store.getters.getSubscriptions || []
        this.trials = this.$store.getters.getTrials || []
        
        // Load all marketplace data
        await this.loadData()
        
        // Manually trigger price fetching for all items after priceHelper is ready
        if (this.allItems && this.allItems.length > 0) {
            this.allItems.forEach(item => {
                this.fetchPrice(item)
            })
        }
        
        // Check if category filter is set from query params
        if (this.$route.query.category) {
            this.filterByCategory(this.$route.query.category)
        }

        // Check if a specific item should be pre-opened via query param
        if (this.$route.query.open) {
            const targetId = this.$route.query.open
            const targetItem = this.allItems.find(function (item) { return item.id === targetId })
            if (targetItem) {
                this.selectItem(targetItem)
            }
        }
        
        // Listen for checkout events
        const checkoutPage = new BroadcastChannel('checkout')
        checkoutPage.onmessage = (msg) => {
            if (msg.data.type == "checkout_finished") {
                this.$store.dispatch("fetchSubscriptions", true)
                this.loadData()
            }
        }
    }
}
</script>
